// The table's state and the events that change it (slices 04, 07 and 08).
//
// One pure reducer, run by the server as authority and by the browser over
// the projection it was given. An object is a TOKEN (creature art, or the
// slice 04 placeholder marker, which is a token without a picture) or a
// PROP (art on the scenery layer, locked, design/11 §8). Nothing about a
// token changes unless the DM asks for it (design/10 §2): no auto-resize, no
// jump when snapping is switched on.
//
// Every object carries `rev`, its own change count. A change names the rev
// it was made against; a change against an older rev was made "at once"
// with another (design/21 §4.2), and the server decides who stands.
//
// A move is a ROUTE, not a destination (design/12 §4): the squares the
// token passed through, recorded because Phase 5 needs them; the last point
// is where it stands. A delete is the DM's alone and recoverable ONCE
// (design/12 §5): the last deletion sits in `undelete` until the DM's own
// next action, which is why the reducer takes the actor's role.
import { defaultRules, normaliseRules, parseRules } from '../rules/tableRules.js';
import { afterDeathSave, afterHurt, diesAt, isDeadAt, parseDeathThrow, parseDying, parseDyingStamp } from './death.js';
import { defaultGrid, parseGrid } from '../grid/grid.js';
import { parseMoveCost } from '../grid/measure.js';
import { floorBelow, isHeight, onViewerFloor, parseFloors, sceneFloors, seenFloors } from './floors.js';
import { cellOf } from '../grid/cells.js';
import { followMoves, sameSoleOwner } from './follow.js';
import { seesAsDm } from '../table/codm.js';
import { APPLY_LIMITS, parseRollRecord } from '../dice/dice.js';
import { parseMeleeAnswer } from '../rules/staged.js';
import { POOL_USES } from '../rules/system.js';
import { foldEvidence, parseEvidence, parseInferred } from '../rules/inferred.js';
import { isAskBody, normaliseAsks, parseAskBody, reduceAsks } from '../dice/asks.js';
import { isRulingBody, normaliseRulings, parseRulingBody, reduceRulings } from '../rules/rulings.js';
import { isAudioBody, normaliseAudio, parseAudioBody, reduceAudio } from './audio.js';
import { isCueBody, normaliseCues, parseCueBody, reduceCues } from './cues.js';
import { isCardBody, normaliseDecks, parseCardBody, reduceDecks } from './cards.js';
import { isLinkBody, normaliseLinks, parseLinkBody, reduceLinks } from './links.js';
import { isHandoutBody, isPinBody, normaliseHandouts, normalisePins, parseHandoutBody, parsePinBody, reduceHandouts, reducePins } from './handouts.js';
import { numberedNames } from './naming.js';
import { applyRewind, isMoment, parseRewindStamp } from './rewind.js';
import { ENCOUNTER_KINDS, withMoveLeft, emptyEncounter, normaliseEncounter, parseEncounterBody, reduceEncounter } from './encounter.js';
import { FOCUS_KINDS, normaliseFocuses, parseFocusBody, reduceFocuses } from './focus.js';
import { AREA_KINDS, normaliseAreas, parseAreaBody, reduceAreas } from './areas.js';
import { DRAW_EVENT_KINDS, normaliseDrawings, parseDrawBody, pruneDrawings, reduceDrawings } from './drawings.js';
import { copyHolding, copyLoadouts, copySpells, parseHolding, parseLoadouts, parseSpells } from './spells.js';
import { copySpent, parseSpent } from '../rules/specials.js';
import { copyAuras, parseAuras } from './auras.js';
import { copyMatrixState, parseMatrixState } from '../rules/matrixHost.js';
import { parseChase } from '../rules/vehicleCombat.js';
import { ZONE_EVENT_KINDS, normaliseZones, parseZoneBody, reduceZones } from './zones.js';
import { WALL_KINDS, normaliseWalls, parseWallBody, reduceWalls } from './walls.js';
import { copySenses, parseSenses } from '../sight/vision.js';
import { dropTokenToggles, parseToggleBody, reduceToggles, TOGGLE_KINDS } from '../rules/starts.js';
import { normaliseWeather, parseWeatherBody, reduceWeather, WEATHER_KINDS } from '../sight/weather.js';
import { LIGHT_KINDS, normaliseLevel, normaliseLights, parseLight, parseLightBody, reduceLight } from '../sight/light.js';
import { VISION_KINDS, applyStamp, defaultVision, normaliseVision, parseVisionBody, readStamp, reduceVision, sightReaches } from '../sight/sight.js';
import { EMPTY_EXTENT, FOG_KINDS, emptyFog, fogHides, normaliseFog, parseFogBody, reduceFog } from './fog.js';
export const LAYERS = ['scenery', 'token', 'dm'];
export const TOKEN_WEAPON_LIMITS = { count: 40, name: 80 };
export function parseTokenWeapons(v) {
    if (!Array.isArray(v) || v.length > TOKEN_WEAPON_LIMITS.count)
        return null;
    const out = [];
    for (const w of v) {
        const r = w;
        if (typeof r !== 'object' || r === null || typeof r.name !== 'string' || !r.name || r.name.length > TOKEN_WEAPON_LIMITS.name)
            return null;
        if ((r.ranged !== undefined && r.ranged !== true) || (r.base !== undefined && !(typeof r.base === 'string' && /^[a-z0-9][a-z0-9-]{0,127}$/.test(r.base))))
            return null;
        out.push({ name: r.name, ...(r.ranged ? { ranged: true } : {}), ...(r.base ? { base: r.base } : {}) });
    }
    return out;
}
/**
 * `count`: the most a token carries. `read` (issue 1534): the most the server reads off a sheet to choose them from — the
 * fields the rules name first (`carriedSheetNumbers`), so a sheet with more numbers than a token holds keeps those.
 */
export const SHEET_NUMBER_LIMITS = { count: 64, read: 256, words: 8, word: 24, lines: 12, line: 40, lineValue: 1000 };
const LINE_BAD = /[\u0000-\u001f\u007f]/;
/** A sheet row's stable id as the site names it (server/utils/sheetRowIds.js): 1 to 64 characters. */
export const isRowId = (v) => typeof v === 'string' && v.length >= 1 && v.length <= 64;
/** A companion link (qa-runs#384), exactly, or null. */
export function parseCompanion(v) {
    const c = v;
    return c && typeof c === 'object' && isUuid(c.character) && isRowId(c.row) ? { character: c.character, row: c.row } : null;
}
/** Whether a token's hit points are kept on a sheet: a character's, or a companion row of its master's (qa-runs#384). */
export const linkedToSheet = (o) => !!o.character || !!o.companion;
/** A sheet number's lines (issue 1505), exactly: up to 12, each a label of 1 to 40 characters and a whole number. */
function parseSheetLines(v) {
    if (!Array.isArray(v) || v.length < 2 || v.length > SHEET_NUMBER_LIMITS.lines)
        return null;
    const out = [];
    for (const x of v) {
        if (typeof x !== 'object' || x === null || Array.isArray(x))
            return null;
        const { label, value, ...rest } = x;
        if (Object.keys(rest).length || typeof label !== 'string' || !label.trim() || label.length > SHEET_NUMBER_LIMITS.line || LINE_BAD.test(label))
            return null;
        if (typeof value !== 'number' || !Number.isInteger(value) || Math.abs(value) > SHEET_NUMBER_LIMITS.lineValue)
            return null;
        out.push({ label, value });
    }
    return out;
}
const SHEET_PURPOSE_RE = /^[a-z][a-z0-9_]{0,62}$/;
const SHEET_WORD_RE = /^[a-z0-9]{1,24}$/;
/** A sheet's numbers (issue 1499), exactly: at most 64, each a purpose, up to 8 words and a bounded number. */
export function parseSheetNumbers(v) {
    if (!Array.isArray(v) || v.length > SHEET_NUMBER_LIMITS.count)
        return null;
    const out = [];
    for (const e of v) {
        if (typeof e !== 'object' || e === null || Array.isArray(e))
            return null;
        const { purpose, words, value, lines, dice, ...rest } = e;
        if (dice !== undefined && !(Array.isArray(dice) && dice.length >= 1 && dice.length <= 4 && dice.every((p) => typeof p === 'object' && p !== null && !Array.isArray(p) && Object.keys(p).length === 2
            && Number.isInteger(p.count) && p.count >= 1 && p.count <= 100 && Number.isInteger(p.sides) && p.sides >= 1 && p.sides <= 1000)))
            return null;
        if (Object.keys(rest).length || typeof purpose !== 'string' || !SHEET_PURPOSE_RE.test(purpose) || !isNum(value))
            return null;
        if (!Array.isArray(words) || words.length > SHEET_NUMBER_LIMITS.words || !words.every((w) => typeof w === 'string' && SHEET_WORD_RE.test(w)))
            return null;
        // Its lines add up to it, or it carries none (issue 1505).
        const parsed = lines === undefined ? undefined : parseSheetLines(lines);
        if (parsed === null || (parsed && parsed.reduce((n, l) => n + l.value, 0) !== value))
            return null;
        out.push({ purpose, words: [...words], value, ...(parsed ? { lines: parsed } : {}), ...(dice ? { dice: dice.map((p) => ({ count: p.count, sides: p.sides })) } : {}) });
    }
    return out;
}
export const FEAT_LIMITS = { count: 64, name: 120 };
/** A sheet's feat names (issue 1500), exactly: at most 64, each 1 to 120 characters. Empty is a sheet that lists none. */
export function parseFeatNames(v) {
    if (!Array.isArray(v) || v.length > FEAT_LIMITS.count || !v.every((n) => typeof n === 'string' && n.length >= 1 && n.length <= FEAT_LIMITS.name))
        return null;
    return [...v];
}
export const RESOURCE_LIMITS = { count: 12 };
export function nonlethalStatus(hp) {
    const n = hp?.nonlethal ?? 0;
    if (!hp || n <= 0)
        return null;
    return n > hp.current ? 'unconscious' : n === hp.current ? 'staggered' : null;
}
/** The words a status is shown in. */
export const NONLETHAL_WORDS = { staggered: 'Staggered', unconscious: 'Unconscious' };
export const FIELDS = ['name', 'hp', 'ac', 'slot1', 'slot2', 'initBonus', 'initRoll', 'conditions', 'resources', 'side', 'stops'];
export const MONSTER_SHARES = { name: true, hp: false, ac: false, slot1: false, slot2: false, initBonus: false, initRoll: true, conditions: true, resources: false, side: false, stops: false };
export const PC_SHARES = { name: true, hp: false, ac: false, slot1: false, slot2: false, initBonus: false, initRoll: false, conditions: false, resources: false, side: false, stops: false };
export const defaultShares = (pc) => ({ ...(pc ? PC_SHARES : MONSTER_SHARES) });
/** A rotation as the wire and the store may name it (issue 1030): a whole number of degrees, 0–359. */
export const isRotation = (v) => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < 360;
const ENTRY_KEY_RE = /^[a-z0-9][a-z0-9._-]{0,199}$/;
const ENTRY_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * A reference from anywhere (the wire, a stored object, the site's answer): the four fields, plus `id` and `book`
 * when present, or null. A present `id` that is not a uuid, or a `book` that is neither a slug nor null, refuses the
 * whole reference (issue 1043): the server writes it, so a bad one is never a half-reference.
 */
export function parseEntryRef(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const r = v;
    const { system, segment, slug, name } = r;
    const key = (k) => typeof k === 'string' && ENTRY_KEY_RE.test(k);
    if (!key(system) || !key(segment) || !key(slug))
        return null;
    if (typeof name !== 'string' || name.trim().length < 1 || name.length > 200 || name.includes('\u0000'))
        return null;
    const out = { system, segment, slug, name };
    if ('id' in r) {
        if (typeof r.id !== 'string' || !ENTRY_ID_RE.test(r.id))
            return null;
        out.id = r.id.toLowerCase();
    }
    if ('book' in r) {
        if (r.book !== null && !key(r.book))
            return null;
        out.book = r.book;
    }
    return out;
}
export const FACT_LIMITS = { count: 40, label: 80, value: 400 };
const isFactText = (v, max) => typeof v === 'string' && v.trim().length >= 1 && v.length <= max && !v.includes('\u0000');
/** A list of revealed facts from anywhere (the wire, a stored object): the two fields of each, or null for any bad shape or a repeated label. */
export function parseFacts(v) {
    if (!Array.isArray(v) || v.length > FACT_LIMITS.count)
        return null;
    const out = [];
    const labels = new Set();
    for (const f of v) {
        if (typeof f !== 'object' || f === null || Array.isArray(f))
            return null;
        const { label, value } = f;
        if (!isFactText(label, FACT_LIMITS.label) || !isFactText(value, FACT_LIMITS.value) || labels.has(label))
            return null;
        labels.add(label);
        out.push({ label, value });
    }
    return out;
}
const storedFacts = (v) => { const f = parseFacts(v); return f && f.length ? f : null; };
/** The marker palette (issue 855): a small fixed set, so a colour is a word everyone's screen draws the same. */
export const MARKERS = ['red', 'blue', 'green', 'yellow', 'purple', 'orange'];
export const isMarker = (v) => typeof v === 'string' && MARKERS.includes(v);
/** Friend, neutral or foe (issue 1046; qa-runs#1157): a token's side, for the rules, the tracker and the board's outline. */
export const DISPOSITIONS = ['friend', 'neutral', 'foe'];
export const isDisposition = (v) => typeof v === 'string' && DISPOSITIONS.includes(v);
/**
 * A token's side (qa-runs#1157): the one set, else its default — a player's token (a player character, or any token a
 * player owns, a familiar included) a friend, any other a foe.
 */
export const dispositionOf = (o) => o.disposition ?? (o.pc || (o.owners?.length ?? 0) > 0 ? 'friend' : 'foe');
/**
 * Who can see a thing (design/11 §4): the DM always; a player a thing that is
 * not hidden, not added while the DM places the table, and — with the fog on
 * (issue 851) — not standing wholly in covered squares. Fog never hides a
 * thing the viewer owns, nor any player character. The ONE place the answer
 * is given: the projection, `decide`'s replies, the turn order and the
 * picture guard all ask here, so GM-010's per-observer reveal is an extension
 * here rather than a rewrite everywhere.
 *
 * `scope` is the state's fog and grid; null says in so many words that no fog
 * applies (a caller holding only objects). Every server path passes the state.
 */
export function canSee(o, viewer, scope) {
    // The DM, and a co-DM shown the DM's board (issue 1159).
    if (seesAsDm(viewer))
        return true;
    // A thing added while the DM places the table has not been shown to anyone yet (issue 811).
    if (o.hidden || (o.held && o.held.shown === null))
        return false;
    if (!scope || (viewer.id !== undefined && o.owners.includes(viewer.id)))
        return true;
    // Floors (qa-runs#1160; design/32 §4): a thing on a floor none of their tokens is on is absent, a character's too —
    // unless one of their tokens sees into its floor through an opening it stands at, or through open air (slice B). What
    // they see there is then decided as on their own floor: the fog, and with vision on their sight.
    if (!onViewerFloor(o, viewer.id ?? '', scope, (id) => seenInto(scope, id)))
        return false;
    const vision = !!(scope.vision?.on && scope.objects && scope.walls);
    // A player character is always seen, except under each character's own sight (§10 Q1, decided B on #1143): there
    // another player's character, like anything they own, is seen only where the viewer's own character sees it.
    if (o.pc && !(vision && scope.vision.sight === 'character'))
        return true;
    // Vision on (issue 1144; design/30 §1.6, §4): a thing is seen when a square of its footprint is in the viewer's
    // sight. Tokens follow sight alone (§10 Q4, decided: the DM's manual reveal shows the map, not who stands there).
    // A player's projected state has no vision, so this never runs on what a player's browser holds.
    if (vision)
        return sightReaches(o, viewer.id ?? '', scope);
    // Where the player sees it: a thing the DM is placing stands where it was shown (issue 811).
    return !fogHides(o.held?.shown ?? { x: o.x, y: o.y }, o.size, scope);
}
/** The floors a player sees into at this state (slice B; `seenFloors`): squares worked out on the scene's grid. */
export function seenInto(state, viewerId) {
    const floors = sceneFloors(state.scene);
    const extent = state.vision?.extent ?? state.fog?.extent ?? EMPTY_EXTENT;
    return seenFloors(state.objects, viewerId, floors, (p) => cellOf(state.grid, extent, p), `${JSON.stringify(state.grid)}|${extent.width}x${extent.height}`);
}
export const OVERLAY_KEYS = ['hp', 'ac', 'slot1', 'slot2', 'conditions'];
export const DEFAULT_OVERLAYS = { hp: true, ac: true, slot1: false, slot2: false, conditions: true };
export const defaultScene = () => ({ snap: true, overlays: { ...DEFAULT_OVERLAYS } });
/** A scene from an older snapshot or event reads with today's defaults for what it lacks. */
export const normaliseScene = (s) => ({
    snap: s?.snap !== false,
    overlays: { ...DEFAULT_OVERLAYS, ...(s?.overlays ?? {}) },
    ...(s?.freeMove === true ? { freeMove: true } : {}),
    ...(s?.floors && s.floors.length > 1 && parseFloors(s.floors) ? { floors: parseFloors(s.floors) } : {}),
});
export const emptyState = () => ({ objects: {}, grid: defaultGrid(), scene: defaultScene(), undelete: null, encounter: emptyEncounter(), focuses: {}, areas: {}, drawings: [], placing: false, fog: emptyFog(), walls: [], vision: defaultVision(), lightLevel: 'bright', lights: [], weather: null, zones: [], asks: {}, rules: defaultRules(), rulings: [], handouts: {}, pins: {}, audio: { music: null, ambience: null, effects: [] }, cues: [], decks: {} });
/** A state read from an older snapshot gets today's defaults for what it lacks. */
export const withDefaults = (state) => ({
    objects: Object.fromEntries(Object.entries(state?.objects ?? {}).map(([id, o]) => [id, normaliseObject(o)])),
    grid: state?.grid ?? defaultGrid(),
    scene: normaliseScene(state?.scene),
    undelete: state?.undelete ? state.undelete.map((o) => normaliseObject(o)) : null,
    encounter: normaliseEncounter(state?.encounter),
    focuses: normaliseFocuses(state?.focuses),
    areas: normaliseAreas(state?.areas),
    drawings: normaliseDrawings(state?.drawings),
    placing: state?.placing === true,
    fog: normaliseFog(state?.fog),
    walls: normaliseWalls(state?.walls),
    vision: normaliseVision(state?.vision),
    lightLevel: normaliseLevel(state?.lightLevel),
    lights: normaliseLights(state?.lights),
    weather: normaliseWeather(state?.weather),
    ...(state?.toggles && typeof state.toggles === 'object' ? { toggles: state.toggles } : {}),
    zones: normaliseZones(state?.zones),
    asks: normaliseAsks(state?.asks),
    rules: normaliseRules(state?.rules),
    rulings: normaliseRulings(state?.rulings),
    handouts: normaliseHandouts(state?.handouts),
    pins: normalisePins(state?.pins),
    ...(() => { const links = normaliseLinks(state?.links); return Object.keys(links).length ? { links } : {}; })(),
    audio: normaliseAudio(state?.audio),
    cues: normaliseCues(state?.cues),
    decks: normaliseDecks(state?.decks),
    ...(() => { const c = state?.chase ? parseChase(state.chase) : null; return c ? { chase: c } : {}; })(),
});
export const DEFAULT_SIZE = { w: 1, h: 1 };
export const OBJECT_LIMITS = { route: 500, batch: 200, copies: 50, size: 50, groupPoints: 800 };
/**
 * A group move's points together. `OBJECT_LIMITS.groupPoints` is the cap `decide` holds it to: a stored event is at
 * most 65,536 characters (vtt/002-events.xml), measured as jsonb's text (a space after every colon and comma):
 * 800 unrounded points over 200 moves is about 56,000, so it fits. Checked in `decide`, not the parser, so the parser still reads what is stored.
 */
export const groupPointsOf = (moves) => moves.reduce((n, m) => n + m.route.length, 0);
/** An object from an older event or snapshot, or a partial add, filled with the defaults a slice 04 marker had. */
export function normaliseObject(o) {
    const kind = o.kind;
    return {
        id: o.id,
        kind: kind === 'prop' ? 'prop' : 'token',
        name: o.name ?? '',
        x: o.x ?? 0,
        y: o.y ?? 0,
        ...(typeof o.floor === 'string' && /^[a-z0-9-]{1,40}$/.test(o.floor) ? { floor: o.floor } : {}),
        ...(isHeight(o.z) ? { z: o.z } : {}),
        size: o.size ? { w: o.size.w, h: o.size.h } : { ...DEFAULT_SIZE },
        fit: o.fit === 'inside' ? 'inside' : 'stretch',
        layer: o.layer && LAYERS.includes(o.layer) ? o.layer : 'token',
        hidden: o.hidden === true,
        owners: Array.isArray(o.owners) ? [...o.owners] : [],
        asset: typeof o.asset === 'string' ? o.asset : null,
        locked: o.locked === true,
        pc: o.pc === true,
        values: o.values ? { ...o.values } : {},
        shares: { ...defaultShares(o.pc === true), ...(o.shares ?? {}) },
        nameplate: o.nameplate !== false,
        rev: typeof o.rev === 'number' ? o.rev : 0,
        ...(o.held ? { held: { shown: o.held.shown ? { x: o.held.shown.x, y: o.held.shown.y } : null, rev: o.held.rev } } : {}),
        ...(typeof o.character === 'string' ? { character: o.character } : {}),
        ...(typeof o.character !== 'string' && parseCompanion(o.companion) ? { companion: parseCompanion(o.companion) } : {}),
        ...((typeof o.character === 'string' || parseCompanion(o.companion)) && o.hpOffSheet === true ? { hpOffSheet: true } : {}),
        ...(Array.isArray(o.departed) && o.departed.some((u) => typeof u === 'string') ? { departed: o.departed.filter((u) => typeof u === 'string') } : {}),
        ...(parseEntryRef(o.entry) ? { entry: parseEntryRef(o.entry) } : {}),
        ...(o.wiki && typeof o.wiki === 'object' && isUuid(o.wiki.pageId) ? { wiki: { pageId: o.wiki.pageId } } : {}),
        dead: o.dead === true,
        marker: isMarker(o.marker) ? o.marker : null,
        ...(isDisposition(o.disposition) ? { disposition: o.disposition } : {}),
        ...(storedFacts(o.facts) ? { facts: storedFacts(o.facts) } : {}),
        ...(o.infer === true ? { infer: true } : {}),
        ...(Array.isArray(o.told) && o.told.some((f) => FIELDS.includes(f)) ? { told: FIELDS.filter((f) => o.told.includes(f)) } : {}),
        ...(o.noAoo === true ? { noAoo: true } : {}),
        ...(o.noExtra === true ? { noExtra: true } : {}),
        ...(parseInferred(o.inferred) ? { inferred: parseInferred(o.inferred) } : {}),
        ...(isRotation(o.rotation) && o.rotation !== 0 ? { rotation: o.rotation } : {}),
        ...(parseLight(o.light) ? { light: parseLight(o.light) } : {}),
        ...(typeof o.follows === 'string' ? { follows: o.follows } : {}),
        ...(typeof o.follows === 'string' && o.mounted === true ? { mounted: true } : {}),
        ...(parseSenses(o.vision)?.length ? { vision: parseSenses(o.vision), ...(o.visionBlock === true ? { visionBlock: true } : {}) } : {}),
        ...(parseSpells(o.spells)?.length ? { spells: parseSpells(o.spells) } : {}),
        ...(parseHolding(o.concentrating) ? { concentrating: parseHolding(o.concentrating) } : {}),
        ...(o.spent && parseSpent(o.spent) ? { spent: parseSpent(o.spent) } : {}),
        ...(parseLoadouts(o.loadouts) ? { loadouts: parseLoadouts(o.loadouts) } : {}),
        ...(typeof o.astral === 'string' && ASTRAL_KEY_RE.test(o.astral) ? { astral: o.astral } : {}),
        ...(parseMatrixState(o.matrix) ? { matrix: parseMatrixState(o.matrix) } : {}),
        ...(o.jacked === true ? { jacked: true } : {}),
        ...(parseAuras(o.auras)?.length ? { auras: parseAuras(o.auras) } : {}),
    };
}
/** A thing the DM has finished placing: as the players will now see it, counted as ONE change since they last did (or new, like a reveal). */
export function release(o) {
    if (!o.held)
        return o;
    const { held, ...rest } = o;
    return { ...rest, rev: held.shown ? held.rev + 1 : 0 };
}
/**
 * What belongs to the table's roll log rather than to a scene (issue 834), as committed: rolls and their records,
 * requests, rulings, handouts, sound, cues and cards. None of it is an act on the board.
 */
export const TABLE_WIDE_KINDS = new Set(['roll', 'roll.amend', 'roll.apply', 'roll.settle', 'log.clear', 'log.note', 'roll.ask', 'roll.ask.close', 'attack.ac', 'attack.answer', 'attack.apply', 'save.bonus', 'save.rethrow', 'save.apply', 'test.karma', 'table.rules', 'ruling.add', 'ruling.keep', 'ruling.remove', 'session.end', 'handout.set', 'handout.send', 'handout.take', 'handout.delete', 'audio.play', 'audio.next', 'audio.stop', 'cue.set', 'cue.remove', 'dialog.say', 'dialog.delete', 'deck.add', 'deck.shuffle', 'deck.remove', 'deck.draw', 'card.discard', 'card.play', 'card.pass', 'card.reveal']);
/**
 * Every kind of event the log keeps, at run time (the co-DM's guard, `server/src/codm-guard.test.ts`, holds each to a
 * power, the server or anyone). The type below fails to compile when a kind is added to `EventBody` and not here.
 */
export const EVENT_KINDS = [
    'area.clear', 'area.clearAll', 'area.set', 'attack.ac', 'attack.answer', 'attack.apply', 'audio.next', 'audio.play', 'audio.stop',
    'card.discard', 'card.pass', 'card.play', 'card.reveal', 'chase.set', 'cue.remove', 'cue.set', 'deck.add', 'deck.draw',
    'deck.remove', 'deck.shuffle', 'dialog.delete', 'dialog.say', 'door.set', 'draw.add', 'draw.addMany', 'draw.erase', 'draw.eraseMine',
    'encounter.action', 'encounter.add', 'encounter.back', 'encounter.deal', 'encounter.dealt', 'encounter.effects', 'encounter.standing',
    'encounter.act', 'encounter.delay', 'encounter.end', 'encounter.opportunity', 'encounter.fire', 'encounter.fired', 'encounter.link', 'encounter.move', 'encounter.next', 'encounter.priority', 'encounter.ready', 'encounter.readyLost', 'encounter.remove',
    'encounter.pool', 'encounter.refresh', 'encounter.roll', 'encounter.rolled', 'encounter.set', 'encounter.sort', 'encounter.spend', 'encounter.start', 'encounter.waitLost', 'encounter.wounds', 'focus.clear',
    'focus.clearAll', 'focus.set', 'fog.cover', 'fog.coverAll', 'fog.reveal', 'fog.revealAll', 'fog.set', 'grid.set',
    'handout.delete', 'handout.send', 'handout.set', 'handout.take', 'light.add', 'light.level', 'light.remove',
    'light.set', 'link.remove', 'link.set', 'log.clear', 'log.note', 'object.add', 'object.assign', 'object.astral', 'object.jack', 'object.auras',
    'object.damage', 'object.dead', 'object.dying', 'object.disposition', 'object.duplicate', 'object.entry', 'object.facts',
    'object.follow', 'object.hide', 'object.infer', 'object.inferred', 'object.layer', 'object.light', 'object.link',
    'object.loadouts', 'object.lock', 'object.marker', 'object.matrix', 'object.move', 'object.nameplate', 'object.noaoo', 'object.noextra', 'object.offsheet', 'object.pc', 'object.remove',
    'object.rename', 'object.resize', 'object.restore', 'object.reveal', 'object.rotate', 'object.share',
    'object.tell',
    'object.sheet', 'object.concentrating', 'object.spells', 'object.spent', 'object.values', 'object.vision', 'object.wiki', 'objects.move', 'owners.prune',
    'pin.audience', 'pin.remove', 'pin.set', 'placement.set', 'roll', 'roll.amend', 'roll.apply', 'roll.ask', 'roll.ask.close', 'roll.settle',
    'ruling.add', 'ruling.keep', 'ruling.remove', 'save.apply', 'save.bonus', 'save.rethrow', 'scene.rewind', 'scene.set',
    'session.end', 'table.rules', 'test.karma', 'toggle.off', 'toggle.on', 'undelete.drop', 'vision.set', 'wall.add', 'wall.remove', 'wall.set', 'weather.set',
    'zone.erase', 'zone.paint', 'zone.remove', 'zone.set',
];
const _everyKind = true;
void _everyKind;
export const SHEET_VALUE_KEYS = ['hp', 'stops', 'ac', 'initBonus', 'resources', 'sheet', 'feats', 'weapons', 'conditions', 'condition_levels', 'dying', 'speed'];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
/** An astral state's key (issue 1531), as the system's data writes it. */
const ASTRAL_KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const isCoord = (v) => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= 1_000_000;
const isName = (v) => typeof v === 'string' && v.length >= 1 && v.length <= 120;
const isRev = (v) => typeof v === 'number' && Number.isInteger(v) && v >= 0;
const isSize = (v) => typeof v === 'object' && v !== null
    && typeof v.w === 'number' && typeof v.h === 'number'
    && v.w > 0 && v.h > 0 && v.w <= OBJECT_LIMITS.size && v.h <= OBJECT_LIMITS.size
    && Number.isFinite(v.w) && Number.isFinite(v.h);
const isFit = (v) => v === 'stretch' || v === 'inside';
const isLayer = (v) => typeof v === 'string' && LAYERS.includes(v);
const isIds = (v, max) => Array.isArray(v) && v.length >= 1 && v.length <= max && v.every(isUuid) && new Set(v).size === v.length;
const isPoint = (v) => typeof v === 'object' && v !== null && isCoord(v.x) && isCoord(v.y);
const isNum = (v) => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= 1_000_000;
const isLabel = (v) => typeof v === 'string' && v.length >= 1 && v.length <= 40;
/** One field of the numbers, exact or undefined (`null` means "remove"). */
function parseValue(key, v) {
    if (v === null)
        return null;
    switch (key) {
        case 'abilityLoss': {
            if (typeof v !== 'object' || Array.isArray(v) || Object.keys(v).length > 12)
                return undefined;
            const out = {};
            for (const [k, l] of Object.entries(v)) {
                if (!/^[a-z0-9_]{1,24}$/.test(k) || typeof l !== 'object' || l === null)
                    return undefined;
                const { damage, drain } = l;
                const fine = (n) => n === undefined || (isWhole(n) && n >= 0 && n <= 999);
                if (!fine(damage) || !fine(drain))
                    return undefined;
                out[k] = { ...(damage ? { damage: damage } : {}), ...(drain ? { drain: drain } : {}) };
            }
            return out;
        }
        case 'hp': {
            if (typeof v !== 'object' || v === null || !isNum(v.current) || !isNum(v.max))
                return undefined;
            const { current, max, nonlethal, temp } = v;
            // The nonlethal count (issue 1502) and temporary hit points: whole numbers, none or more. Absent leaves the token's as it is (object.values).
            if ((nonlethal !== undefined && !(isWhole(nonlethal) && nonlethal >= 0)) || (temp !== undefined && !(isWhole(temp) && temp >= 0)))
                return undefined;
            return { current, max, ...(nonlethal !== undefined ? { nonlethal: nonlethal } : {}), ...(temp !== undefined ? { temp: temp } : {}) };
        }
        case 'stops': return typeof v === 'string' && v.trim().length >= 1 && v.length <= 200 && !/[\u0000-\u0009\u000b-\u001f\u007f]/.test(v) ? v.trim() : undefined;
        case 'ac':
        case 'initBonus':
        case 'initRoll': return isNum(v) ? v : undefined;
        case 'speed':
        case 'fly': return isNum(v) && v > 0 && v <= 10_000 ? v : undefined;
        case 'heavy': return typeof v === 'boolean' ? v : undefined;
        case 'slot1':
        case 'slot2': {
            if (typeof v !== 'object' || v === null)
                return undefined;
            const { label, value, max } = v;
            if (!isLabel(label) || !isNum(value))
                return undefined;
            if (max === undefined || max === null)
                return { label, value };
            return isNum(max) && max > 0 ? { label, value, max } : undefined;
        }
        case 'conditions': return Array.isArray(v) && v.length <= 20 && v.every(isLabel) ? [...v] : undefined;
        case 'condition_levels': return parseConditionLevels(v) ?? undefined;
        case 'dying': return parseDying(v) ?? undefined;
        case 'resources': return parseResources(v) ?? undefined;
        case 'defences': return parseDefences(v) ?? undefined;
        case 'sheet': return parseSheetNumbers(v) ?? undefined;
        case 'feats': return parseFeatNames(v) ?? undefined;
        case 'weapons': return parseTokenWeapons(v) ?? undefined;
        default: return undefined;
    }
}
const VALUE_KEYS = ['hp', 'stops', 'ac', 'defences', 'slot1', 'slot2', 'initBonus', 'initRoll', 'conditions', 'condition_levels', 'dying', 'resources', 'speed', 'fly', 'heavy', 'abilityLoss'];
const RESOURCE_KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const isWhole = (v) => isNum(v) && Number.isInteger(v);
/** A stored body's dying stamp, when it holds a well-formed one. */
const dyingStampOf = (b) => { const d = b.dying === undefined ? null : parseDyingStamp(b.dying); return d ? { dying: d } : {}; };
/** A condition's name as `Values.condition_levels` keys it: its slug. */
export const conditionLevelKey = (name) => name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
/** The sheet purpose a level is held under: a field whose words are the condition's slug. */
export const CONDITION_LEVEL_PURPOSE = 'condition_level';
const LEVEL_KEY_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;
/** A token's levels, exactly: at most a token's twenty conditions, each a whole number from 1 to 99. */
export function parseConditionLevels(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const entries = Object.entries(v);
    if (entries.length > 20 || !entries.every(([k, n]) => LEVEL_KEY_RE.test(k) && isWhole(n) && n >= 1 && n <= 99))
        return null;
    return Object.fromEntries(entries);
}
/** What a token's badge reads for a condition: its level beside its name. */
export const conditionWithLevel = (name, levels) => { const n = levels?.[conditionLevelKey(name)]; return n ? `${name} ${n}` : name; };
/** The most defences a token keeps (qa-runs 402), and the range of each: an attack's typed defence's (`attack.ac`). */
export const DEFENCE_LIMITS = { count: 12, min: -100, max: 1000 };
/** A token's other defences (qa-runs 402), exactly: up to `DEFENCE_LIMITS.count` keys, each a whole number in range. */
export function parseDefences(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const entries = Object.entries(v);
    if (entries.length > DEFENCE_LIMITS.count)
        return null;
    const out = {};
    for (const [k, n] of entries) {
        if (!RESOURCE_KEY_RE.test(k) || !isWhole(n) || n < DEFENCE_LIMITS.min || n > DEFENCE_LIMITS.max)
            return null;
        out[k] = n;
    }
    return out;
}
/** A token's resources (issue 1312), exactly: up to `RESOURCE_LIMITS.count` keys, each a whole current and an optional positive whole maximum. */
export function parseResources(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const entries = Object.entries(v);
    if (entries.length > RESOURCE_LIMITS.count)
        return null;
    const out = {};
    for (const [key, r] of entries) {
        if (!RESOURCE_KEY_RE.test(key) || typeof r !== 'object' || r === null || Array.isArray(r))
            return null;
        const { current, max, ...rest } = r;
        if (Object.keys(rest).length || !isWhole(current))
            return null;
        if (max !== undefined && !(isWhole(max) && max > 0))
            return null;
        out[key] = max === undefined ? { current } : { current, max };
    }
    return out;
}
/**
 * How damage spreads over a token's resources (issue 1312; `damage.to`): the first resource in `order` the token
 * carries takes what it can (down to 0), the rest passing to the next; the last the token carries takes whatever is
 * left, below 0 if need be, as hit points do. Null when the token carries none of them: there is nothing to lower.
 */
export function spreadDamage(resources, order, amount) {
    const held = order.filter((r) => resources?.[r.key]);
    if (!held.length)
        return null;
    const out = [];
    let left = amount;
    held.forEach((r, i) => {
        if (left <= 0)
            return;
        const take = i === held.length - 1 ? left : Math.min(left, Math.max(0, resources[r.key].current));
        if (take > 0)
            out.push({ key: r.key, label: r.label, amount: take });
        left -= take;
    });
    return out;
}
function parseValuesPatch(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const out = {};
    for (const key of Object.keys(v)) {
        if (!VALUE_KEYS.includes(key))
            return null;
        const parsed = parseValue(key, v[key]);
        if (parsed === undefined)
            return null;
        out[key] = parsed;
    }
    return out;
}
/** Whole values (no nulls), for an add. */
function parseValues(v) {
    const patch = parseValuesPatch(v);
    if (!patch)
        return null;
    const out = {};
    for (const [k, val] of Object.entries(patch)) {
        if (val === null)
            return null;
        out[k] = val;
    }
    return out;
}
function parseSharesPatch(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const out = {};
    for (const key of Object.keys(v)) {
        if (!FIELDS.includes(key) || typeof v[key] !== 'boolean')
            return null;
        out[key] = v[key] === true;
    }
    return out;
}
/** A body from the wire: exact shape or null. Untrusted input, always. Older shapes (a move with x and y, a remove with one id, a marker) still read. */
/**
 * An event from the wire or the log. Any kind may carry a SIGHT STAMP (issue 1144; design/30 §3.2): the squares it
 * explored and the doors it saw, per group, which the server alone writes at commit (`commit` drops the wire's).
 */
export function parseEventBody(value) {
    const body = parseBody(value);
    if (!body)
        return null;
    const stamp = readStamp(value);
    return stamp ? { ...body, ...stamp } : body;
}
/** The sight stamp an accepted event carries, if any. */
export const stampOf = (body) => {
    const b = body;
    return b.explored || b.doorsSeen ? { ...(b.explored ? { explored: b.explored } : {}), ...(b.doorsSeen ? { doorsSeen: b.doorsSeen } : {}) } : null;
};
/** The event without a sight stamp: what `commit` starts from, whatever the wire said. */
export function withoutStamp(body) {
    if (!stampOf(body))
        return body;
    const { explored: _e, doorsSeen: _d, ...rest } = body;
    return rest;
}
function parseBody(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const b = value;
    switch (b.kind) {
        case 'grid.set': {
            const grid = parseGrid(b.grid);
            return grid ? { kind: 'grid.set', grid } : null;
        }
        case 'scene.set': {
            const scene = b.scene;
            if (!scene || typeof scene.snap !== 'boolean')
                return null;
            const overlays = {};
            if (scene.overlays !== undefined) {
                if (typeof scene.overlays !== 'object' || scene.overlays === null)
                    return null;
                for (const key of Object.keys(scene.overlays)) {
                    const v = scene.overlays[key];
                    if (!OVERLAY_KEYS.includes(key) || typeof v !== 'boolean')
                        return null;
                    overlays[key] = v;
                }
            }
            if (scene.freeMove !== undefined && typeof scene.freeMove !== 'boolean')
                return null;
            // Floors (qa-runs#1160): 1–10, strict; one floor is the scene with none.
            const floors = scene.floors === undefined ? undefined : parseFloors(scene.floors);
            if (floors === null)
                return null;
            return { kind: 'scene.set', scene: normaliseScene({ snap: scene.snap, overlays: overlays, ...(scene.freeMove === true ? { freeMove: true } : {}), ...(floors && floors.length > 1 ? { floors } : {}) }) };
        }
        case 'object.add': {
            const o = b.object;
            if (!o || (o.kind !== 'token' && o.kind !== 'prop' && o.kind !== 'marker'))
                return null;
            if (!isUuid(o.id) || !isName(o.name) || !isCoord(o.x) || !isCoord(o.y))
                return null;
            if (typeof o.hidden !== 'boolean')
                return null;
            if (!Array.isArray(o.owners) || !o.owners.every(isUuid) || o.owners.length > 16)
                return null;
            const asset = o.asset === undefined || o.asset === null ? null : o.asset;
            if (asset !== null && !isUuid(asset))
                return null;
            if (o.size !== undefined && !isSize(o.size))
                return null;
            if (o.fit !== undefined && !isFit(o.fit))
                return null;
            if (o.layer !== undefined && !isLayer(o.layer))
                return null;
            if (o.locked !== undefined && typeof o.locked !== 'boolean')
                return null;
            if (o.pc !== undefined && typeof o.pc !== 'boolean')
                return null;
            if (o.nameplate !== undefined && typeof o.nameplate !== 'boolean')
                return null;
            if (o.dead !== undefined && typeof o.dead !== 'boolean')
                return null;
            if (o.marker !== undefined && o.marker !== null && !isMarker(o.marker))
                return null;
            const values = o.values === undefined ? {} : parseValues(o.values);
            if (!values)
                return null;
            const shares = o.shares === undefined ? {} : parseSharesPatch(o.shares);
            if (!shares)
                return null;
            // `held` is the server's own bookkeeping, and `character` and `entry` the server's links (issues 848, 1027): never read from the wire.
            const { rev: _rev, spent: _spent, held: _held, character: _character, companion: _companion, entry: _entry, wiki: _wiki, disposition: _disposition, facts: _facts, infer: _infer, told: _told, noAoo: _noAoo, noExtra: _noExtra, inferred: _inferred, follows: _follows, vision: _vision, visionBlock: _visionBlock, spells: _spells, loadouts: _loadouts, auras: _auras, astral: _astral, matrix: _matrix, dead, marker, ...rest } = normaliseObject({ ...o, id: o.id, asset, kind: o.kind === 'prop' ? 'prop' : 'token', values, shares: shares });
            const object = { ...rest, ...(dead ? { dead } : {}), ...(marker ? { marker } : {}) };
            return { kind: 'object.add', object };
        }
        case 'object.move': {
            if (!isUuid(b.id) || !isRev(b.rev))
                return null;
            if (b.route === undefined)
                return isCoord(b.x) && isCoord(b.y) ? { kind: 'object.move', id: b.id, route: [{ x: b.x, y: b.y }], rev: b.rev } : null;
            if (!Array.isArray(b.route) || b.route.length < 1 || b.route.length > OBJECT_LIMITS.route || !b.route.every(isPoint))
                return null;
            const cost = b.cost === undefined ? null : parseMoveCost(b.cost);
            if (b.cost !== undefined && !cost)
                return null;
            // The kind of move it is (qa-runs#1161): one of the system's movement kinds by key, or `rest`, what is left of a Move.
            if (b.move !== undefined && !(typeof b.move === 'string' && /^[a-z][a-z0-9_]{0,62}$/.test(b.move)))
                return null;
            if (b.rest !== undefined && b.rest !== true)
                return null;
            // `left` (qa-runs#1161): what is left of the Move after this one, in squares (below 0: past it), the SERVER's stamp.
            if (b.left !== undefined && !(typeof b.left === 'number' && Number.isFinite(b.left) && Math.abs(b.left) <= 100_000))
                return null;
            if (b.floor !== undefined && !(typeof b.floor === 'string' && /^[a-z0-9-]{1,40}$/.test(b.floor)))
                return null;
            if (b.z !== undefined && b.z !== null && !isHeight(b.z))
                return null;
            return { kind: 'object.move', id: b.id, route: b.route.map((p) => ({ x: p.x, y: p.y })), rev: b.rev, ...(cost ? { cost } : {}), ...(typeof b.move === 'string' ? { move: b.move } : {}), ...(b.rest === true ? { rest: true } : {}), ...(typeof b.left === 'number' ? { left: b.left } : {}), ...(typeof b.floor === 'string' ? { floor: b.floor } : {}), ...(b.z !== undefined ? { z: b.z } : {}) };
        }
        case 'objects.move': {
            if (!Array.isArray(b.moves) || b.moves.length < 1 || b.moves.length > OBJECT_LIMITS.batch)
                return null;
            const moves = [];
            for (const m of b.moves) {
                if (typeof m !== 'object' || m === null || !isUuid(m.id) || !isRev(m.rev))
                    return null;
                if (!Array.isArray(m.route) || m.route.length < 1 || m.route.length > OBJECT_LIMITS.route || !m.route.every(isPoint))
                    return null;
                moves.push({ id: m.id, route: m.route.map((p) => ({ x: p.x, y: p.y })), rev: m.rev });
            }
            return new Set(moves.map((m) => m.id)).size === moves.length ? { kind: 'objects.move', moves } : null;
        }
        case 'object.rename':
            return isUuid(b.id) && isName(b.name) && isRev(b.rev) ? { kind: 'object.rename', id: b.id, name: b.name, rev: b.rev } : null;
        case 'object.reveal':
        case 'object.hide': {
            if (b.ids === undefined)
                return isUuid(b.id) ? { kind: b.kind, ids: [b.id] } : null;
            return isIds(b.ids, OBJECT_LIMITS.batch) ? { kind: b.kind, ids: [...b.ids] } : null;
        }
        case 'object.values': {
            const values = parseValuesPatch(b.values);
            if (b.deadAt !== undefined && !isDeadAt(b.deadAt))
                return null;
            return isUuid(b.id) && values ? { kind: 'object.values', id: b.id, values, ...(b.deadAt !== undefined ? { deadAt: b.deadAt } : {}), ...dyingStampOf(b) } : null;
        }
        case 'object.share': {
            const shares = parseSharesPatch(b.shares);
            return isUuid(b.id) && shares ? { kind: 'object.share', id: b.id, shares } : null;
        }
        case 'object.tell': {
            const tells = parseSharesPatch(b.tells);
            return isUuid(b.id) && tells && Object.keys(tells).length ? { kind: 'object.tell', id: b.id, tells } : null;
        }
        case 'object.pc':
            return isUuid(b.id) && typeof b.pc === 'boolean' ? { kind: 'object.pc', id: b.id, pc: b.pc } : null;
        case 'object.nameplate':
            return isUuid(b.id) && typeof b.shown === 'boolean' ? { kind: 'object.nameplate', id: b.id, shown: b.shown } : null;
        case 'object.assign':
            return isUuid(b.id) && Array.isArray(b.owners) && b.owners.every(isUuid) && b.owners.length <= 16
                ? { kind: 'object.assign', id: b.id, owners: [...b.owners] } : null;
        case 'object.lock':
            return isUuid(b.id) && typeof b.locked === 'boolean' ? { kind: 'object.lock', id: b.id, locked: b.locked } : null;
        case 'object.resize':
            return isUuid(b.id) && isSize(b.size) && isFit(b.fit) ? { kind: 'object.resize', id: b.id, size: { w: b.size.w, h: b.size.h }, fit: b.fit } : null;
        case 'object.layer':
            return isUuid(b.id) && isLayer(b.layer) ? { kind: 'object.layer', id: b.id, layer: b.layer } : null;
        case 'object.dead':
            return isUuid(b.id) && typeof b.dead === 'boolean' ? { kind: 'object.dead', id: b.id, dead: b.dead } : null;
        case 'object.dying': {
            const save = b.save === undefined ? null : parseDeathThrow(b.save);
            return isUuid(b.id) && (b.save === undefined || save) ? { kind: 'object.dying', id: b.id, ...(save ? { save } : {}) } : null;
        }
        case 'object.follow':
            return isUuid(b.id) && (b.follows === null || (isUuid(b.follows) && b.follows !== b.id)) && (b.mounted === undefined || (b.mounted === true && b.follows !== null)) ? { kind: 'object.follow', id: b.id, follows: b.follows, ...(b.mounted ? { mounted: true } : {}) } : null;
        case 'object.disposition':
            return isUuid(b.id) && isDisposition(b.disposition) ? { kind: 'object.disposition', id: b.id, disposition: b.disposition } : null;
        case 'object.facts': {
            const facts = isUuid(b.id) ? parseFacts(b.facts) : null;
            return facts ? { kind: 'object.facts', id: b.id, facts } : null;
        }
        case 'object.infer':
            return isUuid(b.id) && typeof b.on === 'boolean' ? { kind: 'object.infer', id: b.id, on: b.on } : null;
        case 'object.noaoo':
            return isUuid(b.id) && typeof b.on === 'boolean' ? { kind: 'object.noaoo', id: b.id, on: b.on } : null;
        case 'object.noextra':
            return isUuid(b.id) && typeof b.on === 'boolean' ? { kind: 'object.noextra', id: b.id, on: b.on } : null;
        case 'object.inferred': {
            const evidence = parseEvidence(b.evidence);
            return isUuid(b.id) && evidence ? { kind: 'object.inferred', id: b.id, evidence } : null;
        }
        case 'object.auras': {
            const auras = isUuid(b.id) ? parseAuras(b.auras) : null;
            return auras ? { kind: 'object.auras', id: b.id, auras } : null;
        }
        case 'object.spent': {
            const spent = isUuid(b.id) ? parseSpent(b.spent) : null;
            return spent ? { kind: 'object.spent', id: b.id, spent } : null;
        }
        case 'object.concentrating': {
            const holding = b.holding === null ? null : parseHolding(b.holding);
            return isUuid(b.id) && (b.holding === null || holding) ? { kind: 'object.concentrating', id: b.id, holding } : null;
        }
        case 'object.spells': {
            const spells = isUuid(b.id) ? parseSpells(b.spells) : null;
            return spells ? { kind: 'object.spells', id: b.id, spells } : null;
        }
        case 'object.loadouts': {
            const loadouts = isUuid(b.id) ? parseLoadouts(b.loadouts) : null;
            return loadouts ? { kind: 'object.loadouts', id: b.id, loadouts } : null;
        }
        case 'object.rotate':
            return isUuid(b.id) && isRotation(b.rotation) ? { kind: 'object.rotate', id: b.id, rotation: b.rotation } : null;
        case 'chase.set': {
            if (b.chase === null)
                return { kind: 'chase.set', chase: null };
            const c = parseChase(b.chase);
            return c ? { kind: 'chase.set', chase: c } : null;
        }
        case 'object.matrix': {
            if (!isUuid(b.id))
                return null;
            if (b.matrix === null)
                return { kind: 'object.matrix', id: b.id, matrix: null };
            const m = parseMatrixState(b.matrix);
            return m ? { kind: 'object.matrix', id: b.id, matrix: m } : null;
        }
        case 'object.jack':
            return isUuid(b.id) && typeof b.on === 'boolean' ? { kind: 'object.jack', id: b.id, on: b.on } : null;
        case 'object.astral':
            return isUuid(b.id) && (b.state === null || (typeof b.state === 'string' && ASTRAL_KEY_RE.test(b.state))) ? { kind: 'object.astral', id: b.id, state: b.state } : null;
        case 'object.light': {
            if (!isUuid(b.id))
                return null;
            if (b.light === null)
                return { kind: 'object.light', id: b.id, light: null };
            const light = parseLight(b.light);
            return light ? { kind: 'object.light', id: b.id, light } : null;
        }
        case 'object.vision': {
            // `block` is the server's alone (read from a linked stat block); `commit` drops it from anyone else.
            const vision = isUuid(b.id) && (b.block === undefined || b.block === true) ? parseSenses(b.vision) : null;
            return vision ? { kind: 'object.vision', id: b.id, vision, ...(b.block === true ? { block: true } : {}) } : null;
        }
        case 'object.marker':
            return isUuid(b.id) && (b.marker === null || isMarker(b.marker)) ? { kind: 'object.marker', id: b.id, marker: b.marker } : null;
        case 'object.remove': {
            if (b.ids === undefined)
                return isUuid(b.id) ? { kind: 'object.remove', ids: [b.id] } : null;
            return isIds(b.ids, OBJECT_LIMITS.batch) ? { kind: 'object.remove', ids: [...b.ids] } : null;
        }
        case 'object.restore':
            return { kind: 'object.restore' };
        case 'object.duplicate':
            return isUuid(b.id) && isIds(b.ids, OBJECT_LIMITS.copies) && !b.ids.includes(b.id)
                ? { kind: 'object.duplicate', id: b.id, ids: [...b.ids] } : null;
        case 'roll': {
            const roll = parseRollRecord(b.roll);
            return roll ? { kind: 'roll', roll } : null;
        }
        case 'attack.apply': {
            const record = b.record === undefined ? null : parseRollRecord(b.record);
            if (b.record !== undefined && (!record || !record.attack))
                return null;
            if (!isUuid(b.rollId) || !(b.amount === null || (typeof b.amount === 'number' && Number.isInteger(b.amount) && b.amount >= 0 && b.amount <= 100_000)))
                return null;
            if (b.auto !== undefined && typeof b.auto !== 'boolean')
                return null;
            if (b.leftover !== undefined && (b.leftover !== true || b.amount !== null))
                return null;
            return { kind: 'attack.apply', rollId: b.rollId, amount: b.amount, ...(b.auto ? { auto: true } : {}), ...(b.leftover ? { leftover: true } : {}), ...(record ? { record } : {}) };
        }
        case 'save.rethrow': {
            const record = b.record === undefined ? null : parseRollRecord(b.record);
            if ((b.record !== undefined && (!record || (!record.save && !record.trigger))) || !isUuid(b.rollId) || !isUuid(b.target) || typeof b.by !== 'string' || b.by.length > 120)
                return null;
            if (b.end !== undefined && !(typeof b.end === 'number' && Number.isInteger(b.end) && b.end >= 0 && b.end < 64))
                return null;
            return { kind: 'save.rethrow', rollId: b.rollId, target: b.target, by: b.by, ...(b.end !== undefined ? { end: b.end } : {}), ...(b.override === true ? { override: true } : {}), ...(record ? { record } : {}) };
        }
        case 'save.bonus':
        case 'save.apply': {
            // The record is the server's, stamped from the log in `commit`; a stored one is kept, read through its parser.
            const record = b.record === undefined ? null : parseRollRecord(b.record);
            // A trigger's damage settled (issue 1500) carries the trigger's record, as a template's carries the template's.
            if (b.record !== undefined && (!record || (!record.save && !(b.kind === 'save.apply' && record.trigger))))
                return null;
            if (!isUuid(b.rollId) || !isUuid(b.target))
                return null;
            const rec = record ? { record } : {};
            if (b.kind === 'save.bonus')
                return typeof b.bonus === 'number' && Number.isInteger(b.bonus) && b.bonus >= -1000 && b.bonus <= 1000 ? { kind: 'save.bonus', rollId: b.rollId, target: b.target, bonus: b.bonus, ...rec } : null;
            if (!(b.amount === null || (typeof b.amount === 'number' && Number.isInteger(b.amount) && b.amount >= 0 && b.amount <= 100_000)))
                return null;
            if (b.auto !== undefined && typeof b.auto !== 'boolean')
                return null;
            if (b.condition !== undefined && !(typeof b.condition === 'number' && Number.isInteger(b.condition) && b.condition >= 0 && b.condition < 64))
                return null;
            return { kind: 'save.apply', rollId: b.rollId, target: b.target, amount: b.amount, ...(b.auto ? { auto: true } : {}), ...(b.condition !== undefined ? { condition: b.condition } : {}), ...rec };
        }
        case 'table.rules': {
            const rules = parseRules(b.rules);
            return rules ? { kind: 'table.rules', rules } : null;
        }
        case 'object.damage':
            {
                if (!(isUuid(b.id) && isUuid(b.rollId) && typeof b.amount === 'number' && Number.isInteger(b.amount) && b.amount >= 1 && b.amount <= 100_000))
                    return null;
                // Nonlethal (issue 1502): its own count, never spread over resources.
                if (b.damageKind !== undefined && b.to === undefined)
                    return b.damageKind === 'nonlethal' ? { kind: 'object.damage', id: b.id, amount: b.amount, rollId: b.rollId, damageKind: 'nonlethal' } : null;
                // A system's kind of damage (issue 1526), spread down its own chain: a key.
                // Nonlethal never is one: it goes on its own count (issue 1502).
                if (b.damageKind !== undefined && (typeof b.damageKind !== 'string' || !RESOURCE_KEY_RE.test(b.damageKind) || b.damageKind === 'nonlethal'))
                    return null;
                const kindOf = typeof b.damageKind === 'string' ? { damageKind: b.damageKind } : {};
                // The threshold the server stamped (qa-runs 401), on damage to hit points only.
                if (b.deadAt !== undefined && (!isDeadAt(b.deadAt) || b.to !== undefined))
                    return null;
                if (b.to === undefined)
                    return { kind: 'object.damage', id: b.id, amount: b.amount, rollId: b.rollId, ...(b.deadAt !== undefined ? { deadAt: b.deadAt } : {}), ...dyingStampOf(b) };
                // What it lowered (issue 1312): each resource once, by a whole positive amount, adding up to the damage.
                const to = b.to;
                if (!Array.isArray(to) || to.length > RESOURCE_LIMITS.count)
                    return null;
                const parsed = [];
                for (const x of to) {
                    const r = x;
                    if (!r || typeof r.key !== 'string' || !RESOURCE_KEY_RE.test(r.key) || !isLabel(r.label) || !isWhole(r.amount) || r.amount < 1)
                        return null;
                    parsed.push({ key: r.key, label: r.label, amount: r.amount });
                }
                if (new Set(parsed.map((r) => r.key)).size !== parsed.length || parsed.reduce((n, r) => n + r.amount, 0) !== b.amount)
                    return null;
                return { kind: 'object.damage', id: b.id, amount: b.amount, rollId: b.rollId, to: parsed, ...kindOf };
            }
        case 'attack.answer': {
            const record = b.record === undefined ? null : parseRollRecord(b.record);
            // The record is an attack's, or a trigger's chain (issue 1500), as the server stamped it.
            if (b.record !== undefined && (!record || (!record.attack && !record.trigger)))
                return null;
            if (!isUuid(b.rollId))
                return null;
            const given = [b.choice, b.value, b.outcome, b.dice].filter((x) => x !== undefined).length;
            if (given !== 1)
                return null;
            // A staged attack's defender (issue 1527): pool dice, and with them alone the DM's rating and armour.
            if (b.dice !== undefined && !(typeof b.dice === 'number' && Number.isInteger(b.dice) && b.dice >= 0 && b.dice <= 60))
                return null;
            if (b.rating !== undefined && (b.dice === undefined || !(typeof b.rating === 'number' && Number.isInteger(b.rating) && b.rating >= 1 && b.rating <= 60)))
                return null;
            if (b.armour !== undefined && (b.dice === undefined || !(typeof b.armour === 'number' && Number.isInteger(b.armour) && b.armour >= 0 && b.armour <= 100)))
                return null;
            const melee = b.melee === undefined ? undefined : parseMeleeAnswer(b.melee);
            if (melee === null || (melee && b.dice === undefined))
                return null;
            if (b.choice !== undefined && (typeof b.choice !== 'string' || !b.choice.trim() || b.choice.length > 60))
                return null;
            if (b.value !== undefined && !(typeof b.value === 'number' && Number.isInteger(b.value) && Math.abs(b.value) <= 1000))
                return null;
            if (b.outcome !== undefined && b.outcome !== 'hit' && b.outcome !== 'miss' && b.outcome !== 'critical')
                return null;
            return {
                kind: 'attack.answer', rollId: b.rollId, ...(b.choice !== undefined ? { choice: b.choice } : {}), ...(b.value !== undefined ? { value: b.value } : {}),
                ...(b.outcome !== undefined ? { outcome: b.outcome } : {}), ...(record ? { record } : {}),
                ...(b.dice !== undefined ? { dice: b.dice } : {}), ...(b.rating !== undefined ? { rating: b.rating } : {}), ...(b.armour !== undefined ? { armour: b.armour } : {}),
                ...(melee ? { melee } : {}),
            };
        }
        case 'roll.apply': {
            // The record is the roll with its applications, as the server stamped them (qa-runs#406).
            const record = b.record === undefined ? null : parseRollRecord(b.record);
            if (b.record !== undefined && (!record || !record.applied?.length))
                return null;
            if (!isUuid(b.rollId) || !Array.isArray(b.targets) || b.targets.length < 1 || b.targets.length > APPLY_LIMITS.targets || !b.targets.every(isUuid) || new Set(b.targets).size !== b.targets.length)
                return null;
            if (typeof b.heal !== 'boolean' || (b.nonlethal !== undefined && b.nonlethal !== true))
                return null;
            if (b.type !== undefined && (typeof b.type !== 'string' || b.type.trim().length < 1 || b.type.length > APPLY_LIMITS.type))
                return null;
            if (b.heal && (b.type !== undefined || b.nonlethal !== undefined))
                return null;
            return { kind: 'roll.apply', rollId: b.rollId, targets: [...b.targets], heal: b.heal, ...(b.type !== undefined ? { type: b.type.trim() } : {}), ...(b.nonlethal ? { nonlethal: true } : {}), ...(record ? { record } : {}) };
        }
        case 'roll.settle': {
            const record = b.record === undefined ? null : parseRollRecord(b.record);
            if (b.record !== undefined && (!record || !record.applied?.length))
                return null;
            if (!isUuid(b.rollId) || !isUuid(b.target) || (b.amount !== null && !(isWhole(b.amount) && b.amount >= 0 && b.amount <= 100_000)))
                return null;
            return { kind: 'roll.settle', rollId: b.rollId, target: b.target, amount: b.amount, ...(b.auto ? { auto: true } : {}), ...(record ? { record } : {}) };
        }
        case 'test.karma': {
            // The record is the test's, as the server folded the use onto it.
            const record = b.record === undefined ? null : parseRollRecord(b.record);
            if ((b.record !== undefined && (!record || !record.test || !record.test.karma?.length)) || !isUuid(b.rollId) || !POOL_USES.includes(b.use))
                return null;
            return { kind: 'test.karma', rollId: b.rollId, use: b.use, ...(record ? { record } : {}) };
        }
        case 'roll.amend':
        case 'attack.ac': {
            // The record is the server's: kept from a stored body, overwritten in `commit` from the log for one off the wire.
            const record = b.record === undefined ? null : parseRollRecord(b.record);
            if (b.record !== undefined && (!record || !record.attack))
                return null;
            const rec = record ? { record } : {};
            if (b.kind === 'attack.ac')
                return isUuid(b.rollId) && typeof b.ac === 'number' && Number.isInteger(b.ac) && b.ac >= -100 && b.ac <= 1000 ? { kind: 'attack.ac', rollId: b.rollId, ac: b.ac, ...rec } : null;
            return isUuid(b.rollId) && typeof b.total === 'number' && Number.isFinite(b.total) && Math.abs(b.total) <= 1_000_000 ? { kind: 'roll.amend', rollId: b.rollId, total: b.total, ...rec } : null;
        }
        case 'log.clear':
            return { kind: 'log.clear' };
        case 'roll.ask':
        case 'roll.ask.close':
            return parseAskBody(b);
        case 'ruling.add':
        case 'ruling.keep':
        case 'ruling.remove':
        case 'session.end':
            return parseRulingBody(b);
        case 'handout.set':
        case 'handout.send':
        case 'handout.take':
        case 'handout.delete':
            return parseHandoutBody(b);
        case 'pin.set':
        case 'pin.remove':
        case 'pin.audience':
            return parsePinBody(b);
        case 'link.set':
        case 'link.remove':
            return parseLinkBody(b);
        case 'deck.add':
        case 'deck.shuffle':
        case 'deck.remove':
        case 'deck.draw':
        case 'card.discard':
        case 'card.play':
        case 'card.pass':
        case 'card.reveal':
            return parseCardBody(b);
        case 'audio.play':
        case 'audio.next':
        case 'audio.stop':
            return parseAudioBody(b);
        case 'cue.set':
        case 'cue.remove':
        case 'dialog.say':
        case 'dialog.delete':
            return parseCueBody(b);
        case 'owners.prune':
            return isIds(b.userIds, 200) ? { kind: 'owners.prune', userIds: [...b.userIds] } : null;
        case 'scene.rewind': {
            if (!isMoment(b.to) || (b.undo !== undefined && b.undo !== true))
                return null;
            const restored = b.restored === undefined ? undefined : parseRewindStamp(b.restored);
            if (restored === null)
                return null;
            return { kind: 'scene.rewind', to: b.to, ...(b.undo ? { undo: true } : {}), ...(restored ? { restored } : {}) };
        }
        case 'undelete.drop':
            return isUuid(b.asset) ? { kind: 'undelete.drop', asset: b.asset } : null;
        case 'placement.set':
            return typeof b.on === 'boolean' ? { kind: 'placement.set', on: b.on } : null;
        case 'object.link':
            if (!isUuid(b.id) || !(b.character === null || isUuid(b.character)))
                return null;
            if (b.row !== undefined && (b.character === null || !isRowId(b.row)))
                return null;
            return { kind: 'object.link', id: b.id, character: b.character, ...(typeof b.row === 'string' ? { row: b.row } : {}) };
        case 'object.wiki':
            return isUuid(b.id) && (b.pageId === null || isUuid(b.pageId)) ? { kind: 'object.wiki', id: b.id, pageId: b.pageId } : null;
        case 'object.entry': {
            if (!isUuid(b.id) || b.entry === undefined)
                return null;
            if (b.entry === null)
                return { kind: 'object.entry', id: b.id, entry: null };
            const entry = parseEntryRef(b.entry);
            return entry ? { kind: 'object.entry', id: b.id, entry } : null;
        }
        case 'object.offsheet':
            return isUuid(b.id) && typeof b.off === 'boolean' ? { kind: 'object.offsheet', id: b.id, off: b.off } : null;
        case 'object.sheet': {
            if (!isUuid(b.id) || typeof b.values !== 'object' || b.values === null || Array.isArray(b.values))
                return null;
            const values = {};
            for (const key of Object.keys(b.values)) {
                if (!SHEET_VALUE_KEYS.includes(key))
                    return null;
                const v = parseValue(key, b.values[key]);
                if (v === undefined || v === null)
                    return null;
                values[key] = v;
            }
            if (b.deadAt !== undefined && !isDeadAt(b.deadAt))
                return null;
            return { kind: 'object.sheet', id: b.id, values, ...(b.deadAt !== undefined ? { deadAt: b.deadAt } : {}), ...dyingStampOf(b) };
        }
        case 'log.note':
            if (b.rule !== undefined && b.rule !== true)
                return null;
            return isUuid(b.ownerId) && typeof b.note === 'string' && b.note.trim().length >= 1 && b.note.length <= 200 ? { kind: 'log.note', note: b.note, ownerId: b.ownerId, ...(b.rule ? { rule: true } : {}), ...(b.announce ? { announce: true } : {}) } : null;
        default:
            if (typeof b.kind === 'string' && ENCOUNTER_KINDS.includes(b.kind))
                return parseEncounterBody(b);
            if (typeof b.kind === 'string' && FOCUS_KINDS.includes(b.kind))
                return parseFocusBody(b);
            if (typeof b.kind === 'string' && AREA_KINDS.includes(b.kind))
                return parseAreaBody(b);
            if (typeof b.kind === 'string' && DRAW_EVENT_KINDS.includes(b.kind))
                return parseDrawBody(b);
            if (typeof b.kind === 'string' && FOG_KINDS.includes(b.kind))
                return parseFogBody(b);
            if (typeof b.kind === 'string' && WALL_KINDS.includes(b.kind))
                return parseWallBody(b);
            if (typeof b.kind === 'string' && VISION_KINDS.includes(b.kind))
                return parseVisionBody(b);
            if (typeof b.kind === 'string' && LIGHT_KINDS.includes(b.kind))
                return parseLightBody(b);
            if (typeof b.kind === 'string' && WEATHER_KINDS.includes(b.kind))
                return parseWeatherBody(b);
            if (typeof b.kind === 'string' && TOGGLE_KINDS.includes(b.kind))
                return parseToggleBody(b);
            if (typeof b.kind === 'string' && ZONE_EVENT_KINDS.includes(b.kind))
                return parseZoneBody(b);
            return null;
    }
}
export const isEncounterBody = (body) => ENCOUNTER_KINDS.includes(body.kind);
export const isFocusBody = (body) => FOCUS_KINDS.includes(body.kind);
export const isAreaBody = (body) => AREA_KINDS.includes(body.kind);
export const isDrawBody = (body) => DRAW_EVENT_KINDS.includes(body.kind);
export const isFogBody = (body) => FOG_KINDS.includes(body.kind);
export const isWallBody = (body) => WALL_KINDS.includes(body.kind);
export const isVisionBody = (body) => VISION_KINDS.includes(body.kind);
export const isLightBody = (body) => LIGHT_KINDS.includes(body.kind);
export const isZoneBody = (body) => ZONE_EVENT_KINDS.includes(body.kind);
export const isWeatherBody = (body) => WEATHER_KINDS.includes(body.kind);
/** A Toggle switched on or off (qa-runs#371): the token's own action, its owner's or the DM's. */
export const isToggleBody = (body) => TOGGLE_KINDS.includes(body.kind);
/** The one object an event acts on, or null for a creation, the grid, the scene, a restore, the turn order. */
export function targetOf(body) {
    if (isToggleBody(body))
        return body.token;
    if (isRulingBody(body) || isHandoutBody(body) || isPinBody(body) || isLinkBody(body) || isAudioBody(body) || isCueBody(body) || isCardBody(body))
        return null;
    if (isEncounterBody(body) || isFocusBody(body) || isAreaBody(body) || isDrawBody(body) || isFogBody(body) || isWallBody(body) || isVisionBody(body) || isLightBody(body) || isWeatherBody(body) || isZoneBody(body))
        return null;
    switch (body.kind) {
        case 'object.add':
        case 'grid.set':
        case 'scene.set':
        case 'object.restore':
        case 'object.remove':
        case 'object.reveal':
        case 'object.hide':
        case 'roll':
        case 'roll.amend':
        case 'attack.ac':
        case 'attack.answer':
        case 'test.karma':
        case 'attack.apply':
        case 'save.bonus':
        case 'save.rethrow':
        case 'save.apply':
        case 'roll.apply':
        case 'roll.settle':
        case 'table.rules':
        case 'roll.ask':
        case 'roll.ask.close':
        case 'log.clear':
        case 'owners.prune':
        case 'undelete.drop':
        case 'placement.set':
        case 'objects.move':
        case 'log.note':
        case 'scene.rewind':
        case 'chase.set': return null;
        default: return body.id;
    }
}
/** Every object an event touches, for what a viewer is told. */
export function idsOf(body) {
    if (isToggleBody(body))
        return [body.token];
    if (isRulingBody(body) || isHandoutBody(body) || isPinBody(body) || isLinkBody(body) || isAudioBody(body) || isCueBody(body) || isCardBody(body))
        return [];
    if (isEncounterBody(body) || isFocusBody(body) || isAreaBody(body) || isDrawBody(body) || isFogBody(body) || isWallBody(body) || isVisionBody(body) || isLightBody(body) || isWeatherBody(body) || isZoneBody(body))
        return [];
    switch (body.kind) {
        case 'object.add': return [body.object.id];
        case 'object.remove':
        case 'object.reveal':
        case 'object.hide': return body.ids;
        case 'objects.move': return body.moves.map((m) => m.id);
        case 'object.duplicate': return [body.id, ...body.ids];
        case 'grid.set':
        case 'scene.set':
        case 'object.restore':
        case 'roll':
        case 'roll.amend':
        case 'attack.ac':
        case 'attack.answer':
        case 'test.karma':
        case 'attack.apply':
        case 'save.bonus':
        case 'save.rethrow':
        case 'save.apply':
        case 'roll.apply':
        case 'roll.settle':
        case 'table.rules':
        case 'roll.ask':
        case 'roll.ask.close':
        case 'log.clear':
        case 'owners.prune':
        case 'undelete.drop':
        case 'placement.set':
        case 'log.note':
        case 'scene.rewind':
        case 'chase.set': return [];
        default: return [body.id];
    }
}
/** The move's destination: the route's last point. */
export const destinationOf = (route) => route[route.length - 1] ?? { x: 0, y: 0 };
/**
 * Apply an ACCEPTED event. Pure: a new state, the old one untouched. The
 * reducer trusts the event — acceptance (permission, the tie, existence,
 * the lock) is the server's `decide`; here an event on a missing object is
 * a no-op so a projected stream that omits some events still reduces
 * cleanly. `actorRole` is who acted: the DM's own next action, and nothing
 * anyone else does, clears the undelete (design/12 §5).
 */
/**
 * Hit points set by hand or read from a sheet, with the nonlethal count (issue 1502): one the change names is kept (none
 * at 0); one it does not name stays as the token had it, lowered by what the hit points rose when `heal` (the rules:
 * healing that restores hit points removes as much nonlethal damage). None left is absent.
 */
function withNonlethal(next, was, heal) {
    const { nonlethal: named, temp, ...kept } = next;
    // Temporary hit points named are replaced, never added to; absent, the token's stay; none left is absent.
    const t = temp ?? was?.temp ?? 0;
    const hp = t > 0 ? { ...kept, temp: t } : kept;
    const rose = heal && was ? Math.max(0, hp.current - was.current) : 0;
    const n = named ?? Math.max(0, (was?.nonlethal ?? 0) - rose);
    return n > 0 ? { ...hp, nonlethal: n } : hp;
}
export function applyEvent(state, body, actorRole) {
    // What the event explored (issue 1144; design/30 §3.2) is only added: a replay never redoes the geometry.
    const reduced = applyStamp(reduce(state, body, actorRole), stampOf(body));
    // A Move, or a move of what is left of one, stamped with what remains (qa-runs#1161): the turn's record holds it.
    const next = body.kind === 'object.move' && body.left !== undefined && reduced.encounter ? { ...reduced, encounter: withMoveLeft(reduced.encounter, body.id, body.left) } : reduced;
    // A throw, a log marker, pointing at a place or switching placement mode is not an action on the board: it leaves the DM's undelete standing.
    // Nor is anything of the table's own log (`TABLE_WIDE_KINDS`): sound, cues, decks, handouts, rulings — on a one-scene
    // table they share the scene's log, and music played or a cue fired by a player's move took the undelete (the review of 2026-10-10).
    // Nor are the server's own writes from a character's sheet (issue 848): a refresh when the DM arrives is not the DM acting.
    const boardAction = !TABLE_WIDE_KINDS.has(body.kind) && body.kind !== 'object.remove' && body.kind !== 'object.restore' && body.kind !== 'roll' && body.kind !== 'roll.amend' && body.kind !== 'attack.ac' && body.kind !== 'attack.answer' && body.kind !== 'test.karma' && body.kind !== 'roll.apply' && body.kind !== 'roll.settle' && body.kind !== 'attack.apply' && body.kind !== 'save.bonus' && body.kind !== 'save.rethrow' && body.kind !== 'save.apply' && body.kind !== 'table.rules' && body.kind !== 'object.damage' && body.kind !== 'object.inferred' && !isAskBody(body) && !isRulingBody(body) && !isHandoutBody(body) && !isPinBody(body) && !isLinkBody(body) && !isCardBody(body) && body.kind !== 'log.clear' && body.kind !== 'owners.prune' && body.kind !== 'undelete.drop' && body.kind !== 'placement.set' && body.kind !== 'log.note' && body.kind !== 'object.sheet' && body.kind !== 'object.offsheet' && body.kind !== 'object.link' && body.kind !== 'object.entry' && !isFocusBody(body) && !isAreaBody(body) && !isDrawBody(body) && !isFogBody(body) && !isWallBody(body) && !isVisionBody(body) && !isLightBody(body) && !isWeatherBody(body) && !isZoneBody(body) && !isToggleBody(body);
    if (actorRole === 'dm' && boardAction && next.undelete)
        return { ...next, undelete: null };
    return next;
}
/** Placing, a thing the DM adds starts unseen by the players until they are done (issue 811); a hidden one needs no hold. */
const heldIfPlacing = (state, o) => {
    const { held: _held, ...rest } = o;
    return state.placing && !o.hidden ? { ...rest, held: { shown: null, rev: 0 } } : rest;
};
/** Where a move leaves a thing. Placing, the DM's move of a thing the players can see is held: they keep seeing it where it stood (issue 811). */
function moved(state, current, route, actorRole) {
    const d = destinationOf(route);
    const hold = state.placing && actorRole === 'dm' && !current.hidden && !current.held ? { held: { shown: { x: current.x, y: current.y }, rev: current.rev } } : {};
    return { ...current, ...hold, x: d.x, y: d.y, rev: current.rev + 1 };
}
/** A move's floor and height (qa-runs#1160): a new floor stands it on that floor unless a height comes with it; null stands it. */
function lifted(o, body) {
    if (body.floor === undefined && body.z === undefined)
        return o;
    const { floor: _f, z: _z, ...rest } = o;
    const floor = body.floor ?? o.floor;
    const z = body.z === undefined ? (body.floor !== undefined && body.floor !== o.floor ? undefined : o.z) : body.z ?? undefined;
    return { ...rest, ...(floor ? { floor } : {}), ...(z !== undefined ? { z } : {}) };
}
/**
 * Floors changed (qa-runs#1160; design/32 §2): a thing on a removed floor lands on the floor below it, standing (the
 * browser moves them first where the DM chose otherwise), and the removed floor's walls go.
 */
function reflowFloors(state, scene) {
    const old = sceneFloors(state.scene);
    const now = sceneFloors(scene);
    const gone = old.filter((f) => !now.some((n) => n.id === f.id)).map((f) => f.id);
    if (!gone.length || !state.scene.floors)
        return { ...state, scene };
    const objects = { ...state.objects };
    for (const [id, o] of Object.entries(objects)) {
        if (!o.floor || !gone.includes(o.floor))
            continue;
        const { z: _z, floor: _f, ...rest } = o;
        const to = floorBelow(old, o.floor, now);
        objects[id] = { ...rest, ...(scene.floors ? { floor: to } : {}), rev: o.rev + 1 };
    }
    const walls = (state.walls ?? []).filter((w) => !w.floor || !gone.includes(w.floor));
    return { ...state, scene, objects, walls };
}
/** A link whose two tokens no longer have one sole owner between them ends (issue 1031): owners changed under it. In place, on a copy. */
function unlinkBroken(objects) {
    for (const [id, o] of Object.entries(objects)) {
        const leader = o.follows ? objects[o.follows] : undefined;
        if (leader && !sameSoleOwner(o, leader)) {
            const { follows: _f, mounted: _m, ...rest } = o;
            objects[id] = rest;
        }
    }
}
function reduce(state, body, actorRole) {
    // `?? []`: a state from before issue 1136 (or the DM's projected one) may lack the rulings.
    if (isRulingBody(body))
        return { ...state, rulings: reduceRulings(state.rulings ?? [], body) };
    // `?? {}`: a state from before issue 1154, or a projected one, lacks the handouts and the pins.
    if (isHandoutBody(body))
        return { ...state, handouts: reduceHandouts(state.handouts ?? {}, body) };
    if (isPinBody(body))
        return { ...state, pins: reducePins(state.pins ?? {}, body) };
    if (isLinkBody(body)) {
        const links = reduceLinks(state.links ?? {}, body);
        if (Object.keys(links).length)
            return { ...state, links };
        const { links: _l, ...rest } = state;
        return rest;
    }
    if (isCardBody(body))
        return { ...state, decks: reduceDecks(state.decks ?? {}, body) };
    if (isAudioBody(body))
        return { ...state, audio: reduceAudio(state.audio, body) };
    if (isCueBody(body))
        return { ...state, cues: reduceCues(state.cues, body) };
    if (isEncounterBody(body))
        return { ...state, encounter: reduceEncounter(state.encounter, body) };
    if (body.kind === 'chase.set') {
        if (body.chase)
            return { ...state, chase: body.chase };
        const { chase: _c, ...rest } = state;
        return rest;
    }
    if (isFocusBody(body))
        return { ...state, focuses: reduceFocuses(state.focuses, body) };
    if (isAreaBody(body))
        return { ...state, areas: reduceAreas(state.areas, body) };
    // `?? []`: the DM's browser reduces over its projected state, which an older snapshot may lack the drawings in.
    if (isDrawBody(body))
        return { ...state, drawings: reduceDrawings(state.drawings ?? [], body) };
    // `?? emptyFog()`: the DM's browser reduces over its projected state, which an older snapshot or a test fixture may lack the fog in.
    if (isFogBody(body))
        return { ...state, fog: reduceFog(state.fog ?? emptyFog(), body, state.grid) };
    // `?? []`: the DM's browser reduces over its projected state, which an older snapshot may lack the walls in.
    if (isWallBody(body))
        return { ...state, walls: reduceWalls(state.walls ?? [], body) };
    if (isLightBody(body))
        return { ...state, ...reduceLight(state, body) };
    if (isWeatherBody(body))
        return { ...state, weather: reduceWeather(body) };
    if (isToggleBody(body))
        return { ...state, toggles: reduceToggles(state.toggles, body) };
    // `?? []`: an older snapshot (or the DM's projected state from one) lacks the zones.
    if (isZoneBody(body))
        return { ...state, zones: reduceZones(state.zones ?? [], body) };
    if (isVisionBody(body))
        return reduceVision({ ...state, vision: state.vision ?? defaultVision(), fog: state.fog ?? emptyFog() }, body);
    const objects = { ...state.objects };
    switch (body.kind) {
        // The squares move with the grid, so the masked map changes: its version moves too while the fog is on.
        case 'grid.set': return { ...state, grid: body.grid, ...(state.fog?.on ? { fog: { ...state.fog, rev: state.fog.rev + 1 } } : {}) };
        case 'scene.set': return reflowFloors(state, normaliseScene(body.scene));
        case 'object.add': {
            const o = normaliseObject({ ...body.object, rev: 0 });
            // Objects placed on the DM layer start hidden (design/11 §3): the only thing that makes that layer special.
            objects[o.id] = heldIfPlacing(state, o.layer === 'dm' ? { ...o, hidden: true } : o);
            return { ...state, objects };
        }
        case 'object.remove': {
            const removed = [];
            for (const id of body.ids) {
                const o = objects[id];
                if (o) {
                    removed.push(o);
                    delete objects[id];
                }
            }
            // A removed token's lit toggles go with it (qa-runs#371).
            let toggles = state.toggles;
            if (toggles)
                for (const o of removed)
                    toggles = dropTokenToggles(toggles, o.id);
            return { ...state, objects, undelete: removed.length ? removed : state.undelete, ...(toggles ? { toggles } : {}) };
        }
        case 'object.restore': {
            // Brought back after the DM finished placing, a thing is released as it would have been then.
            for (const o of state.undelete ?? [])
                if (!objects[o.id])
                    objects[o.id] = state.placing ? o : release(o);
            return { ...state, objects, undelete: null };
        }
        case 'object.reveal':
        case 'object.hide': {
            const hidden = body.kind === 'object.hide';
            // Revealed, a thing's change count starts again (slice 15's leaks audit, design/22 §7): a goblin the DM moved
            // twelve times while it was hidden must not arrive at rev 13 and say so. A player names a rev only once they
            // have been shown the thing, so the tie still works from here; hiding still counts, since players lose sight.
            // A reveal is the DM saying "now": a thing being placed is shown where it stands, and its hold ends (issue 811).
            for (const id of body.ids) {
                const o = objects[id];
                if (o && o.hidden !== hidden) {
                    const { held: _held, ...rest } = o;
                    objects[id] = { ...rest, hidden, rev: hidden ? o.rev + 1 : 0 };
                }
            }
            return { ...state, objects };
        }
        case 'object.duplicate': {
            const source = objects[body.id];
            if (!source)
                return state;
            const names = numberedNames(source.name, Object.values(objects).map((o) => o.name), body.ids.length);
            if (names.source !== source.name)
                objects[source.id] = { ...source, name: names.source, rev: source.rev + 1 };
            const step = state.grid.x.basePitch * source.size.w;
            body.ids.forEach((id, k) => {
                if (objects[id])
                    return;
                // A copy stands for no character (issue 848): one character is one token's.
                // Nor does a copy follow anything (issue 1031): one familiar is one link.
                // Nor does a copy know what was revealed of its source (issue 1057): a reveal covers that token only.
                // Nor what the players worked out of it (issue 1133): a copy is another creature, holding no spell of its source's.
                const { character: _character, companion: _companion, follows: _follows, mounted: _mounted, facts: _facts, infer: _infer, inferred: _inferred, concentrating: _concentrating, ...copy } = source;
                objects[id] = heldIfPlacing(state, { ...copy, id, name: names.copies[k], x: source.x + step * (k + 1), owners: [...source.owners], rev: 0 });
            });
            return { ...state, objects };
        }
        // A throw answering a group request marks its roller (issue 1049); asks open and close. Nothing else of the log is state.
        case 'roll':
        case 'roll.ask':
        case 'roll.ask.close': {
            const asks = reduceAsks(state.asks ?? {}, body);
            return asks === state.asks ? state : { ...state, asks };
        }
        case 'roll.amend':
        case 'attack.ac':
        case 'attack.answer':
        case 'test.karma':
        case 'attack.apply':
        case 'save.bonus':
        case 'save.rethrow':
        case 'save.apply':
        case 'roll.apply':
        case 'roll.settle':
        case 'log.clear':
        case 'log.note': return state;
        case 'table.rules': return { ...state, rules: { ...body.rules } }; // the log is read from the events themselves; the board's state does not change
        case 'owners.prune': {
            const gone = new Set(body.userIds);
            const strip = (o) => {
                if (!o.owners.some((u) => gone.has(u)))
                    return o;
                const left = o.owners.filter((u) => gone.has(u));
                const departed = o.character ? [...new Set([...(o.departed ?? []), ...left])] : o.departed;
                return { ...o, owners: o.owners.filter((u) => !gone.has(u)), rev: o.rev + 1, ...(departed?.length ? { departed } : {}) };
            };
            for (const [id, o] of Object.entries(objects))
                objects[id] = strip(o);
            unlinkBroken(objects);
            // Their drawings stay on the map, belonging to nobody (issue 1032): the DM's to erase.
            return { ...state, objects, undelete: state.undelete ? state.undelete.map(strip) : null, drawings: pruneDrawings(state.drawings ?? [], gone) };
        }
        // A rewind (issue 1169) applies what the server stamped, and nothing else: the past is never worked out again here.
        case 'scene.rewind': return applyRewind(state, body.restored);
        case 'undelete.drop': {
            // Only the things wearing that picture (issue 1020); the rest of the undelete stands, and none left is none.
            const kept = (state.undelete ?? []).filter((o) => o.asset !== body.asset);
            return { ...state, undelete: kept.length ? kept : null };
        }
        case 'objects.move': {
            // Each mover's followers (issue 1031) come along in the same event, read from the state before it.
            for (const m of [...body.moves, ...followMoves(state, body.moves)]) {
                const o = objects[m.id];
                if (o)
                    objects[m.id] = moved(state, o, m.route, actorRole);
            }
            return { ...state, objects };
        }
        case 'placement.set': {
            if (body.on)
                return { ...state, placing: true };
            for (const [id, o] of Object.entries(objects))
                if (o.held)
                    objects[id] = release(o);
            return { ...state, objects, placing: false };
        }
        default: break;
    }
    const current = objects[body.id];
    if (!current)
        return state;
    switch (body.kind) {
        case 'object.move': {
            objects[body.id] = lifted(moved(state, current, body.route, actorRole), body);
            // A familiar follows its owner (issue 1031): the followers travel in this same event.
            for (const m of followMoves(state, [body])) {
                const o = objects[m.id];
                if (o)
                    objects[m.id] = moved(state, o, m.route, actorRole);
            }
            break;
        }
        case 'object.rename':
            objects[body.id] = { ...current, name: body.name, rev: current.rev + 1 };
            break;
        case 'object.assign': {
            const { departed: was, ...rest } = current;
            const departed = (was ?? []).filter((u) => !body.owners.includes(u));
            // A linked token given to somebody who neither owned it nor left owning it (issue 1499): its sheet's numbers are
            // the character's owner's, never the new owner's to be sent — they go, until the sheet is read again for it.
            const stranger = linkedToSheet(current) && body.owners.some((u) => !current.owners.includes(u) && !(was ?? []).includes(u));
            const values = stranger && (rest.values.sheet || rest.values.feats || rest.values.weapons) ? (({ sheet: _s, feats: _f, weapons: _w, ...v }) => v)(rest.values) : rest.values;
            objects[body.id] = { ...rest, values, owners: [...body.owners], rev: current.rev + 1, ...(departed.length ? { departed } : {}) };
            unlinkBroken(objects);
            break;
        }
        case 'object.values': {
            const values = { ...current.values };
            for (const [k, v] of Object.entries(body.values)) {
                if (v === null)
                    delete values[k];
                else
                    values[k] = v;
            }
            if (body.values.hp)
                values.hp = withNonlethal(body.values.hp, current.values.hp, true);
            // A linked token's current hit points changed by anyone but its owner (issue 1052; #850, Tom's A): the DM's change
            // stays at the table, marked, until the owner accepts it or the DM reads the sheet again. An owner — always a
            // player — changes only their own token's, and the server says whether that reached the sheet (`object.offsheet`).
            const offSheet = (linkedToSheet(current) && actorRole !== 'player' && values.hp && (values.hp.current !== current.values.hp?.current || (values.hp.nonlethal ?? 0) !== (current.values.hp?.nonlethal ?? 0) || (values.hp.temp ?? 0) !== (current.values.hp?.temp ?? 0)))
                // The same for a linked character's own resources (qa-runs 1170: a monitor's boxes), changed by anyone but its owner.
                || (!!current.character && actorRole !== 'player' && !!body.values.resources && JSON.stringify(values.resources ?? {}) !== JSON.stringify(current.values.resources ?? {}));
            // Hit points lowered to the stamped threshold or below (qa-runs 401): dead, without a click.
            const fell = body.dying && values.hp ? afterHurt({ ...values, ...(current.values.hp ? { hp: current.values.hp } : {}) }, values.hp, body.dying) : null;
            const dies = diesAt(current.values.hp, values.hp, body.deadAt) || fell?.dead === true;
            objects[body.id] = { ...current, values: fell ? fell.values : values, rev: current.rev + 1, ...(offSheet ? { hpOffSheet: true } : {}), ...(dies ? { dead: true } : {}) };
            break;
        }
        case 'object.sheet': {
            // The sheet's hit points read onto the token: they are on the sheet again (issue 1052).
            const { hpOffSheet: _off, ...rest } = current;
            const read = { ...current.values, ...body.values };
            // A sheet that keeps no nonlethal count leaves the token's (issue 1502).
            if (body.values.hp)
                read.hp = withNonlethal(body.values.hp, current.values.hp, false);
            const fell = body.dying && read.hp ? afterHurt({ ...read, ...(current.values.hp ? { hp: current.values.hp } : {}) }, read.hp, body.dying) : null;
            const dies = diesAt(current.values.hp, read.hp, body.deadAt) || fell?.dead === true;
            objects[body.id] = { ...(body.values.hp ? rest : current), values: fell ? fell.values : read, rev: current.rev + 1, ...(dies ? { dead: true } : {}) };
            break;
        }
        case 'object.dying': {
            if (!body.save)
                return state;
            const saved = afterDeathSave(current.values, body.save);
            objects[body.id] = { ...current, values: saved.values, rev: current.rev + 1, ...(linkedToSheet(current) && body.save.word === 'regain_1' ? { hpOffSheet: true } : {}), ...(saved.dead ? { dead: true } : {}) };
            break;
        }
        case 'object.offsheet': {
            // Bookkeeping like a link (issue 1052): the count stays, so nobody's move is refused for it.
            const { hpOffSheet: _off, ...rest } = current;
            objects[body.id] = body.off && linkedToSheet(current) ? { ...rest, hpOffSheet: true } : rest;
            break;
        }
        case 'object.damage': {
            // A system's own resources (issue 1312): each lowered by what the server stamped, hit points untouched.
            if (body.to) {
                const have = current.values.resources;
                if (!have)
                    return state;
                const resources = { ...have };
                for (const r of body.to)
                    if (resources[r.key])
                        resources[r.key] = { ...resources[r.key], current: resources[r.key].current - r.amount };
                // A runner's monitor (qa-runs 1170) is on their sheet: damage to it waits for the owner's Accept, as hit points do.
                objects[body.id] = { ...current, values: { ...current.values, resources }, rev: current.rev + 1, ...(current.character ? { hpOffSheet: true } : {}) };
                break;
            }
            // Current hit points only (decision 26: the maximum is the DM's, set once); a token with none is unchanged.
            const hp = current.values.hp;
            if (!hp)
                return state;
            // Nonlethal damage (issue 1502, Tom's A) adds to the count beside the hit points and lowers none of them.
            if (body.damageKind === 'nonlethal') {
                objects[body.id] = { ...current, values: { ...current.values, hp: { ...hp, nonlethal: (hp.nonlethal ?? 0) + body.amount } }, rev: current.rev + 1, ...(linkedToSheet(current) ? { hpOffSheet: true } : {}) };
                break;
            }
            // Damage on a linked character stays at the table until its owner accepts it (issue 1052; #850, Tom's A).
            // Taken to the threshold the server stamped, or below (qa-runs 401): dead, without a click.
            // Temporary hit points go first (`damage.temporary`), and a token left with none has no such key.
            const { temp, ...rest } = hp;
            const absorbed = Math.min(temp ?? 0, Math.max(0, body.amount));
            const left = (temp ?? 0) - absorbed;
            const lowered = { ...rest, current: hp.current - (body.amount - absorbed), ...(left > 0 ? { temp: left } : {}) };
            const fell = body.dying ? afterHurt(current.values, lowered, body.dying) : null;
            objects[body.id] = { ...current, values: fell ? fell.values : { ...current.values, hp: lowered }, rev: current.rev + 1, ...(linkedToSheet(current) && body.amount !== 0 ? { hpOffSheet: true } : {}), ...(diesAt(hp, lowered, body.deadAt) || fell?.dead ? { dead: true } : {}) };
            break;
        }
        case 'object.link': {
            // A link is bookkeeping, not a change anyone contests: the count stays, so nobody's move is refused for it. A
            // change of link starts the token on its (new) sheet, or none: the mark goes (issue 1052).
            // Who left owning it (issue 1446) goes with the link: they left owning that character's token, not this one.
            // The old sheet's numbers for the rules (issue 1499) go with the old link: the new sheet's are read onto it.
            // A companion's link (qa-runs#384) names its master and the row: the token stands for that row, not the character.
            const { character: _character, companion: _companion, hpOffSheet: _off, departed: _departed, ...rest } = current;
            const same = body.row ? current.companion?.character === body.character && current.companion?.row === body.row : body.character === current.character;
            const values = !same && (rest.values.sheet || rest.values.feats || rest.values.weapons) ? (({ sheet: _s, feats: _f, weapons: _w, ...v }) => v)(rest.values) : rest.values;
            objects[body.id] = !body.character ? { ...rest, values } : body.row ? { ...rest, values, companion: { character: body.character, row: body.row } } : { ...rest, values, character: body.character };
            break;
        }
        case 'object.entry': {
            // Bookkeeping like a character's link (issue 1027): the count stays. What was revealed of the entry (issue 1057)
            // goes with it — unlinked, or linked to another — and stays when the same entry is written again.
            const { entry: _entry, facts, ...rest } = current;
            const same = !!body.entry && !!current.entry && (body.entry.id && current.entry.id ? body.entry.id === current.entry.id : body.entry.segment === current.entry.segment && body.entry.slug === current.entry.slug);
            objects[body.id] = { ...rest, ...(body.entry ? { entry: { ...body.entry } } : {}), ...(same && facts ? { facts } : {}) };
            break;
        }
        case 'object.share':
            objects[body.id] = { ...current, shares: { ...current.shares, ...body.shares }, rev: current.rev + 1 };
            break;
        case 'object.tell': {
            // Like a share (issue 1575): a change a player may now be sent, so the count moves. None told is absent.
            const { told: _t, ...rest } = current;
            const told = FIELDS.filter((f) => body.tells[f] ?? current.told?.includes(f) ?? false);
            objects[body.id] = { ...rest, ...(told.length ? { told } : {}), rev: current.rev + 1 };
            break;
        }
        case 'object.pc':
            objects[body.id] = { ...current, pc: body.pc, shares: current.pc === body.pc ? current.shares : defaultShares(body.pc), rev: current.rev + 1 };
            break;
        case 'object.nameplate':
            objects[body.id] = { ...current, nameplate: body.shown, rev: current.rev + 1 };
            break;
        case 'object.lock':
            objects[body.id] = { ...current, locked: body.locked, rev: current.rev + 1 };
            break;
        case 'object.resize':
            objects[body.id] = { ...current, size: { ...body.size }, fit: body.fit, rev: current.rev + 1 };
            break;
        case 'object.layer':
            objects[body.id] = { ...current, layer: body.layer, rev: current.rev + 1 };
            break;
        case 'object.dead':
            objects[body.id] = { ...current, dead: body.dead, rev: current.rev + 1 };
            break;
        case 'object.marker':
            objects[body.id] = { ...current, marker: body.marker, rev: current.rev + 1 };
            break;
        case 'object.rotate': {
            // Counted like death (issue 1030): players see it, so the tick tells an owner nothing they are not shown. 0 is absent.
            const { rotation: _r, ...rest } = current;
            objects[body.id] = body.rotation === 0 ? { ...rest, rev: current.rev + 1 } : { ...rest, rotation: body.rotation, rev: current.rev + 1 };
            break;
        }
        case 'object.light': {
            // A token's light (issue 1145): seen by whoever sees the token, so its count ticks as a turn's does. null: none.
            const { light: _l, ...rest } = current;
            objects[body.id] = body.light ? { ...rest, light: { ...body.light }, rev: current.rev + 1 } : { ...rest, rev: current.rev + 1 };
            break;
        }
        case 'object.vision': {
            // A token's senses (issue 1146): bookkeeping like its spells — the count stays (only the owners and the DM are
            // told it). Empty is absent: normal sight.
            const { vision: _v, visionBlock: _b, ...rest } = current;
            objects[body.id] = body.vision.length ? { ...rest, vision: copySenses(body.vision), ...(body.block ? { visionBlock: true } : {}) } : rest;
            break;
        }
        case 'object.follow': {
            // Counted (issue 1031): the owner is told it, so their tick follows what they see.
            const { follows: _f, mounted: _m, ...rest } = current;
            objects[body.id] = body.follows === null ? { ...rest, rev: current.rev + 1 } : { ...rest, follows: body.follows, ...(body.mounted ? { mounted: true } : {}), rev: current.rev + 1 };
            break;
        }
        case 'object.disposition': {
            // Bookkeeping like an entry's link (issue 1046): the count stays, so an owner is not sent a tick for a change
            // they cannot see (issue 856). Every side is stored as chosen, neutral too (qa-runs#1157).
            objects[body.id] = { ...current, disposition: body.disposition };
            break;
        }
        case 'object.facts': {
            // Bookkeeping like a side (issue 1046): the count stays, so a reveal never ties with a player's move. Empty is absent.
            const { facts: _f, ...rest } = current;
            objects[body.id] = body.facts.length ? { ...rest, facts: body.facts.map((f) => ({ label: f.label, value: f.value })) } : rest;
            break;
        }
        case 'object.wiki': {
            // Bookkeeping like the switch below (issue 1155): the count stays, so a link never ties with an owner's move.
            const { wiki: _w, ...rest } = current;
            objects[body.id] = body.pageId ? { ...rest, wiki: { pageId: body.pageId } } : rest;
            break;
        }
        case 'object.infer': {
            // Bookkeeping like a reveal (issue 1133): the count stays. Off is absent; what was worked out stays either way.
            const { infer: _i, ...rest } = current;
            objects[body.id] = body.on ? { ...rest, infer: true } : rest;
            break;
        }
        case 'object.matrix': {
            // Bookkeeping like a side (issue 1532): the count stays, so a player, who is never sent the host, is sent no change.
            const { matrix: _m, ...rest } = current;
            objects[body.id] = body.matrix ? { ...rest, matrix: copyMatrixState(body.matrix) } : rest;
            break;
        }
        case 'object.jack': {
            // Bookkeeping like an astral state (issue 1173): the count stays. Out is absent.
            const { jacked: _j, ...rest } = current;
            objects[body.id] = body.on ? { ...rest, jacked: true } : rest;
            break;
        }
        case 'object.astral': {
            // Bookkeeping like a side (issue 1531): the count stays, so a player who is not sent the state is sent no change.
            const { astral: _a, ...rest } = current;
            objects[body.id] = body.state ? { ...rest, astral: body.state } : rest;
            break;
        }
        case 'object.noaoo': {
            // Bookkeeping like a side (issue 1500): the count stays. Off — the creature takes them again — is absent.
            const { noAoo: _n, ...rest } = current;
            objects[body.id] = body.on ? { ...rest, noAoo: true } : rest;
            break;
        }
        case 'object.noextra': {
            // Bookkeeping like `object.noaoo` (issue 1159). Off — feats add their attacks again — is absent.
            const { noExtra: _n, ...rest } = current;
            objects[body.id] = body.on ? { ...rest, noExtra: true } : rest;
            break;
        }
        case 'object.inferred':
            objects[body.id] = { ...current, inferred: foldEvidence(current.inferred, body.evidence) };
            break;
        case 'object.spells': {
            // Bookkeeping like a side (issue 1130): the count stays, so writing a spell never ties with the owner's own move. Empty is absent.
            const { spells: _s, ...rest } = current;
            objects[body.id] = body.spells.length ? { ...rest, spells: copySpells(body.spells) } : rest;
            break;
        }
        case 'object.concentrating': {
            const { concentrating: _c, ...rest } = current;
            objects[body.id] = body.holding ? { ...rest, concentrating: copyHolding(body.holding) } : rest;
            break;
        }
        case 'object.spent': {
            // The DM's own count (`rules/specials.ts`): bookkeeping like its spells. Empty is absent.
            const { spent: _s, ...rest } = current;
            objects[body.id] = Object.keys(body.spent).length ? { ...rest, spent: copySpent(body.spent) } : rest;
            break;
        }
        case 'object.auras': {
            // Bookkeeping like its spells (issue 1150): the count stays, so an owner's move never ties with it. Empty is absent.
            const { auras: _a, ...rest } = current;
            objects[body.id] = body.auras.length ? { ...rest, auras: copyAuras(body.auras) } : rest;
            break;
        }
        case 'object.loadouts': {
            const { loadouts: _l, ...rest } = current;
            objects[body.id] = body.loadouts.list.length || body.loadouts.active ? { ...rest, loadouts: copyLoadouts(body.loadouts) } : rest;
            break;
        }
    }
    return { ...state, objects };
}
//# sourceMappingURL=objects.js.map